import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  startTransition
} from 'react'
import { flushSync } from 'react-dom'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  getPrices,
  getDraftById,
  exportDraftToExcel,
  uploadDraftToGcpCSV,
  extractFilenameFromContentDisposition,
  reAllocateProducts,
  exportDraftToDBExcel,
  uploadDraftToGcpDBExcel,
  validateCustomerCodes,
  validateBranchCodes,
  getGradeOptions,
  getSizeOptions
} from '../../services/Draft'
import './RightPanelPopup.css'
import { eventBus, EVENTS } from '../../utils/EventBus'
import {
  DistributionManager,
  removeColumnOccurrence
} from '../../utils/DistributionManager'
import { ColumnManager } from '../../utils/ColumnManager'
import { getColumnType } from '../../utils/columnType'
import { validateCustomerCodeLocal } from '../../utils/customerValidation'
import DistributionTable from './components/DistributionTable'
import LogPanel from './components/LogPanel'
import OrderPanel from './components/OrderPanel'
import Toolbar from './components/Toolbar'
import {
  transformToApiFormat,
  createEditPayload,
  normalizeItemForUI,
  getSafeProductName
} from '../../services/distribution/transformPayload'
import { validatePriceGroups } from '../../services/distribution/validatePriceGroups'
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useUpdateDraftDistribution } from '../../hooks/DistributionTable/useUpdateDraftDistribution'
import { useToast } from '../../components/ui/Toast/useToast'
import Toast from '../../components/ui/Toast/Toast'
import { useAuth } from '../../context/AuthContext'
import { getRowLabel, formatCode } from '../../utils/formatCode'
import { reorderSelection } from './components/DistributionTable/utils/reorderSelection'
import {
  buildValueFilterOptions,
  normalizeValueFilterValue,
  pruneUnavailableFilterValues
} from './components/DistributionTable/utils/valueFilter'

import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog'

const formatDatePlusOne = (dateString) => {
  if (!dateString) return ''
  const normalizedString = dateString.replace(/\//g, '-')
  const datePart = normalizedString.slice(0, 10)
  const parts = datePart.split('-')
  if (parts.length !== 3) return ''

  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1
  const day = parseInt(parts[2], 10)

  const date = new Date(year, month, day)
  date.setDate(date.getDate() + 1)

  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}/${mm}/${dd}`
}

const FIXED_FIELDS = [
  'shipping_mark',
  'grade',
  'size',
  'quantity_unit',
  'listing_quantity',
  'input_price',
  'output_price'
]

const RightPanelPopup = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast, showToast, hideToast } = useToast()

  const { username } = useAuth()

  const distributionManager = useRef(new DistributionManager(username)).current
  const columnManager = useRef(new ColumnManager()).current

  const [selectedNyukaNumbers, setSelectedNyukaNumbers] = useState({})
  const [inventoryData, setInventoryData] = useState({})
  const [changeLog, setChangeLog] = useState([])
  const [isLogOpen, setIsLogOpen] = useState(false)
  const [isOrderOpen, setIsOrderOpen] = useState(false)
  const [logPanelWidth, setLogPanelWidth] = useState(200)
  const [forceUpdate, setForceUpdate] = useState(0)
  const [additionalRows, setAdditionalRows] = useState([])
  const [currentLogIndex, setCurrentLogIndex] = useState(0)
  const [farmFilterValues, setFarmFilterValues] = useState([])
  const [gradeFilterValues, setGradeFilterValues] = useState([])
  const [sizeFilterValues, setSizeFilterValues] = useState([])
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const filterRequestRef = useRef(0)
  const pendingFilterRef = useRef(null)

  const [confirmDialogForConfirm, setConfirmDialogForConfirm] = useState({
    open: false,
    onConfirm: null
  })

  const [confirmDialogForSave, setConfirmDialogForSave] = useState({
    open: false,
    onConfirm: null
  })

  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(new Set())
  const [rowOrder, setRowOrder] = useState([])
  const [hiddenRowKeys, setHiddenRowKeys] = useState(new Set())
  const [hiddenHeaderFieldKeys, setHiddenHeaderFieldKeys] = useState(new Set())
  const [gradeOptions, setGradeOptions] = useState([])
  const [sizeOptions, setSizeOptions] = useState([])
  const [draftAccess, setDraftAccess] = useState({
    editable: true,
    confirmState: 'draft',
    canExportExcel: false,
    canExportCsv: false,
    canExportDbExcel: false
  })
  const [confirmedLocked, setConfirmedLocked] = useState(false)

  const [salesDate, setSalesDate] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)
  const [initialUiMetadataPresent, setInitialUiMetadataPresent] =
    useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [splitDialogState, setSplitDialogState] = useState({
    open: false,
    item: null,
    priceId: null,
    maxQuantity: null,
    quantity: '',
    error: ''
  })

  const getFixedRowKey = useCallback((item, field) => {
    if (!item) return item?.niukabango
    if (field === 'shipping_mark' || field === 'grade' || field === 'size') {
      return (
        item._parentNiukabango ??
        item._originalNiukabango ??
        String(item.niukabango).split('__')[0]
      )
    }
    return item.niukabango
  }, [])

  const isReadOnly = useMemo(
    () =>
      searchParams.get('readOnly') === '1' ||
      confirmedLocked ||
      draftAccess.editable === false ||
      draftAccess.confirmState === 'confirmed',
    [searchParams, confirmedLocked, draftAccess]
  )

  const canDownloadExcel = useMemo(
    () => !hasUnsavedChanges && Boolean(draftAccess.canExportExcel),
    [hasUnsavedChanges, draftAccess.canExportExcel]
  )

  const canDownloadFinalFiles = useMemo(
    () =>
      !hasUnsavedChanges &&
      Boolean(draftAccess.canExportCsv) &&
      Boolean(draftAccess.canExportDbExcel),
    [hasUnsavedChanges, draftAccess.canExportCsv, draftAccess.canExportDbExcel]
  )

  // Synchronously compute the flattened list of all base items and additional items
  const getFlattenedItemsSynchronously = (invData, addRows) => {
    const selectedItems = []

    Object.entries(selectedNyukaNumbers).forEach(
      ([hinmeiCode, niukabangos]) => {
        niukabangos.forEach((niukabango) => {
          const niukaItem = invData[hinmeiCode]?.find(
            (item) => String(item.niukabango) === String(niukabango)
          )

          if (!niukaItem) return

          if (Array.isArray(niukaItem.items) && niukaItem.items.length > 0) {
            niukaItem.items.forEach((detail, itemIndex) => {
              if (!detail || typeof detail !== 'object') return

              const uniqueNiukabango =
                niukaItem.items.length > 1
                  ? `${niukaItem.niukabango}__${itemIndex}`
                  : niukaItem.niukabango

              const priceGroup =
                Array.isArray(detail.price_groups) &&
                detail.price_groups.length > 0
                  ? detail.price_groups
                  : [
                      {
                        input_price: detail.input_price,
                        output_price: detail.output_price,
                        profit: detail.profit,
                        distribution_result: detail.distribution_result || [],
                        _priceId: `p0`
                      }
                    ]

              selectedItems.push({
                ...detail,
                hinmeiCode: detail.hinmeiCode ?? hinmeiCode,
                niukabango: uniqueNiukabango,
                _originalNiukabango: niukaItem.niukabango,
                _itemIndex: itemIndex,
                price_groups: priceGroup
              })
            })
          } else {
            const priceGroup =
              Array.isArray(niukaItem.price_groups) &&
              niukaItem.price_groups.length > 0
                ? niukaItem.price_groups
                : [
                    {
                      input_price: niukaItem.input_price,
                      output_price: niukaItem.output_price,
                      profit: niukaItem.profit,
                      distribution_result: niukaItem.distribution_result || [],
                      _priceId: `p0`
                    }
                  ]

            selectedItems.push({
              ...niukaItem,
              hinmeiCode: niukaItem.hinmeiCode ?? hinmeiCode,
              niukabango: niukaItem.niukabango,
              _originalNiukabango: niukaItem.niukabango,
              _itemIndex: 0,
              price_groups: priceGroup
            })
          }
        })
      }
    )

    const allItemsSynchronous = [...selectedItems, ...addRows]
    return flattenItems(allItemsSynchronous)
  }

  // Capture a full, immutable deep clone of the current application state
  const captureAppState = (
    invData = inventoryData,
    addRows = additionalRows,
    rOrder = rowOrder
  ) => {
    const synchronousOriginalItems = getFlattenedItemsSynchronously(
      invData,
      addRows
    )

    return {
      inventoryData: structuredClone(invData),
      additionalRows: structuredClone(addRows),
      rowOrder: structuredClone(rOrder),
      distributionManagerData: structuredClone(distributionManager.data),
      distributionManagerOriginalItems: structuredClone(
        distributionManager.reconcileItems(synchronousOriginalItems)
      ),
      distributionManagerDraftCells: structuredClone(
        distributionManager.draftCells
      ),
      distributionManagerResetCells: Array.from(distributionManager.resetCells)
    }
  }

  // Restore the application state from a captured snapshot with detailed debugging logs
  const restoreAppState = (state, type = 'unknown') => {
    console.group(`[UNDO/REDO DEBUG - SPLIT ROW] Restore Action: ${type}`)
    console.log('Restoring to exact snapshot state...')

    // Check object identity comparisons to verify reference purity
    console.log(
      'Reference Check - inventoryData identical?',
      inventoryData === state.inventoryData
    )
    console.log(
      'Reference Check - additionalRows identical?',
      additionalRows === state.additionalRows
    )
    console.log(
      'Reference Check - rowOrder identical?',
      rowOrder === state.rowOrder
    )

    const clonedInventory = structuredClone(state.inventoryData)
    const clonedAdditional = structuredClone(state.additionalRows)
    const clonedRowOrder = structuredClone(state.rowOrder)

    setInventoryData(clonedInventory)
    setAdditionalRows(clonedAdditional)
    setRowOrder(clonedRowOrder)

    distributionManager.data = structuredClone(state.distributionManagerData)
    distributionManager.originalItems = structuredClone(
      state.distributionManagerOriginalItems
    )
    distributionManager.draftCells = structuredClone(
      state.distributionManagerDraftCells
    )
    distributionManager.resetCells = new Set(
      state.distributionManagerResetCells
    )

    console.log('Restored rowOrder:', clonedRowOrder)

    // Debug logging for verified quantities on all active split rows
    try {
      const getQuantities = (items) => {
        const results = []
        items.forEach((item) => {
          if (item.price_groups) {
            item.price_groups.forEach((g) => {
              results.push({
                priceId: g._priceId,
                total_quantity: g.total_quantity,
                quantity: g.quantity
              })
            })
          }
        })
        return results
      }

      const flatInv = []
      Object.keys(clonedInventory).forEach((k) => {
        clonedInventory[k].forEach((nyuka) => {
          if (nyuka.items) {
            flatInv.push(...nyuka.items)
          }
        })
      })

      console.log('Restored Base Price Groups Qty:', getQuantities(flatInv))
      console.log(
        'Restored Additional Price Groups Qty:',
        getQuantities(clonedAdditional)
      )
    } catch (e) {
      console.warn('Could not print detail debug Qty', e)
    }

    // Trigger reset events to force re-computation and visual rendering
    eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
    setForceUpdate((v) => v + 1)

    console.groupEnd()
  }

  const getParentRowKey = useCallback((proxyItem) => {
    const parentNyuka =
      proxyItem._originalNiukabango ||
      proxyItem._parentNiukabango ||
      String(proxyItem.niukabango || '').split('__')[0]
    return `${proxyItem.hinmeiCode}::${parentNyuka}`
  }, [])

  const getRowKeyForParent = useCallback(
    (item) => `${item.hinmeiCode}::${item.niukabango}`,
    []
  )

  useEffect(() => {
    let cancelled = false

    Promise.all([getGradeOptions(), getSizeOptions()])
      .then(([grades, sizes]) => {
        if (cancelled) return
        setGradeOptions(Array.isArray(grades) ? grades : [])
        setSizeOptions(Array.isArray(sizes) ? sizes : [])
      })
      .catch((error) => {
        console.error('Failed to fetch grade/size options:', error)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleHideRow = useCallback(
    (rowKeys, item) => {
      if (isReadOnly) return
      const keysToHide = Array.isArray(rowKeys) ? rowKeys : [rowKeys]
      if (keysToHide.length === 0) return

      setHiddenRowKeys((prev) => {
        const next = new Set(prev)
        keysToHide.forEach((k) => next.add(k))
        return next
      })
      setHasUnsavedChanges(true)

      const label =
        keysToHide.length > 1
          ? `${keysToHide.length}行`
          : getRowLabel(keysToHide[0])
      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${label} を非表示にしました`,
        up: () =>
          setHiddenRowKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.add(k))
            return next
          }),
        down: () =>
          setHiddenRowKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.delete(k))
            return next
          })
      })
    },
    [distributionManager, isReadOnly]
  )

  const handleHideHeaderField = useCallback(
    (fieldKeys) => {
      if (isReadOnly) return
      const keysToHide = Array.isArray(fieldKeys) ? fieldKeys : [fieldKeys]
      if (keysToHide.length === 0) return

      setHiddenHeaderFieldKeys((prev) => {
        const next = new Set(prev)
        keysToHide.forEach((k) => next.add(k))
        return next
      })
      setHasUnsavedChanges(true)

      const label = keysToHide.length > 1 ? `${keysToHide.length}行` : 'ヘッダー行'
      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${label} を非表示にしました`,
        up: () =>
          setHiddenHeaderFieldKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.add(k))
            return next
          }),
        down: () =>
          setHiddenHeaderFieldKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.delete(k))
            return next
          })
      })
    },
    [distributionManager, isReadOnly]
  )

  const handleHideColumn = useCallback(
    (columnKeys) => {
      if (isReadOnly) return
      const keysToHide = Array.isArray(columnKeys) ? columnKeys : [columnKeys]
      if (keysToHide.length === 0) return

      setHiddenColumnKeys((prev) => {
        const next = new Set(prev)
        keysToHide.forEach((k) => next.add(k))
        return next
      })
      setHasUnsavedChanges(true)

      const label =
        keysToHide.length > 1
          ? `${keysToHide.length}列`
          : formatCode(keysToHide[0])
      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${label} の 列を非表示にしました`,
        up: () =>
          setHiddenColumnKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.add(k))
            return next
          }),
        down: () =>
          setHiddenColumnKeys((prev) => {
            const next = new Set(prev)
            keysToHide.forEach((k) => next.delete(k))
            return next
          })
      })
    },
    [distributionManager, isReadOnly]
  )

  const handleUnhideItem = useCallback(
    (type, key) => {
      if (isReadOnly) return
      if (type === 'row') {
        setHiddenRowKeys((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
        setHasUnsavedChanges(true)

        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] ${getRowLabel(key)} の 行を再表示しました`,
          up: () => {
            setHiddenRowKeys((prev) => {
              const next = new Set(prev)
              next.delete(key)
              return next
            })
            setForceUpdate((v) => v + 1)
          },
          down: () => {
            setHiddenRowKeys((prev) => new Set(prev).add(key))
            setForceUpdate((v) => v + 1)
          }
        })
      } else if (type === 'headerField') {
        setHiddenHeaderFieldKeys((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
        setHasUnsavedChanges(true)

        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] ヘッダー行を再表示しました`,
          up: () => {
            setHiddenHeaderFieldKeys((prev) => {
              const next = new Set(prev)
              next.delete(key)
              return next
            })
            setForceUpdate((v) => v + 1)
          },
          down: () => {
            setHiddenHeaderFieldKeys((prev) => new Set(prev).add(key))
            setForceUpdate((v) => v + 1)
          }
        })
      } else {
        setHiddenColumnKeys((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
        setHasUnsavedChanges(true)

        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] ${formatCode(key)} の 列を再表示しました`,
          up: () => {
            setHiddenColumnKeys((prev) => {
              const next = new Set(prev)
              next.delete(key)
              return next
            })
            setForceUpdate((v) => v + 1)
          },
          down: () => {
            setHiddenColumnKeys((prev) => new Set(prev).add(key))
            setForceUpdate((v) => v + 1)
          }
        })
      }
    },
    [distributionManager, isReadOnly]
  )

  const [orders, setOrders] = useState([])
  // Full order list sent to the optimizer (input_data.data.customers).
  // `orders` (result.orders) only holds orders left unallocated, so it is kept for column typing only.
  const [orderCustomers, setOrderCustomers] = useState([])
  const [sellerCode, setSellerCode] = useState('')
  const [productName, setProductName] = useState('')
  const [saleMan, setSaleMan] = useState('')

  const productNameByHic = useMemo(() => {
    const map = {}
    Object.entries(inventoryData).forEach(([hinmeiCode, niukaItems]) => {
      const name = (niukaItems || []).find(
        (item) => item?.product_name || item?.hinmeiName
      )
      if (name) map[hinmeiCode] = name.product_name || name.hinmeiName
    })
    return map
  }, [inventoryData])

  const { mutateAsync: updateDraft } = useUpdateDraftDistribution()

  const todayString = useMemo(() => {
    const today = new Date()
    return `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`
  }, [])

  useEffect(() => {
    window.dm = distributionManager
  }, [distributionManager])

  useEffect(() => {
    const syncIndex = () =>
      setCurrentLogIndex(distributionManager.nextLogIndex - 1)
    const syncIndexAndRefreshItems = () => {
      syncIndex()
      setForceUpdate((revision) => revision + 1)
    }
    const unsubUndo = eventBus.on(EVENTS.LOG_UNDO, syncIndexAndRefreshItems)
    const unsubRedo = eventBus.on(EVENTS.LOG_REDO, syncIndexAndRefreshItems)
    const unsubAdded = eventBus.on(EVENTS.LOG_ENTRY_ADDED, syncIndex)
    const unsubCleared = eventBus.on(EVENTS.LOG_CLEARED, syncIndex)
    const unsubReorder = eventBus.on(EVENTS.COLUMN_REORDERED, (data) => {
      if (isReadOnly) return
      if (data?.isImport) return
      const oldConfig = data?.oldConfig || columnManager.exportConfig()
      setHasUnsavedChanges(true)

      // We wrap the addLogEntry in a small timeout to ensure ColumnManager has finished its internal update
      setTimeout(() => {
        const newConfig = columnManager.exportConfig()
        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] 列の並び順を変更しました`,
          up: () => {
            columnManager.importConfig(newConfig)
            setForceUpdate((v) => v + 1)
          },
          down: () => {
            columnManager.importConfig(oldConfig)
            setForceUpdate((v) => v + 1)
          }
        })
      }, 0)
    })
    const unsubReset = eventBus.on(
      EVENTS.DISTRIBUTION_RESET,
      syncIndexAndRefreshItems
    )
    return () => {
      unsubUndo()
      unsubRedo()
      unsubAdded()
      unsubCleared()
      unsubReorder()
      unsubReset()
    }
  }, [distributionManager, columnManager, isReadOnly])

  const autoSelectedAllRef = useRef(false)
  const hasAppliedInitialColumnSortRef = useRef(false)
  // While true, the next allItems render rebuilds distributionManager from the
  // React rows (setOriginalData) instead of keeping its own allocations.
  const isFirstLoadRef = useRef(true)
  // Bumped on every successful save so in-flight focus refreshes started
  // before the save do not overwrite the saved state with older data.
  const savedDraftVersionRef = useRef(0)
  // After 保存 the table keeps running on the pre-save managers (so Undo still
  // works), while sessionStorage holds the saved draft. Their column keys can
  // differ (the save renumbers them), so do not mix the two on reload.
  const sessionHoldsSavedDraftRef = useRef(false)

  const saveSessionState = useCallback(() => {
    const payloadKey = searchParams.get('payloadKey')
    if (!payloadKey) return

    try {
      const stored = sessionStorage.getItem(payloadKey)
      const payload = stored ? JSON.parse(stored) : {}

      payload.selectedNyukaNumbers = selectedNyukaNumbers
      if (!sessionHoldsSavedDraftRef.current) {
        payload.inventoryData = inventoryData
        payload.distributionData = distributionManager.getData()
        payload.additionalRows = additionalRows
        payload.columnConfig = columnManager.exportConfig()
        payload.hiddenColumnKeys = Array.from(hiddenColumnKeys)
      }
      payload.rowOrder = rowOrder
      payload.hiddenRowKeys = Array.from(hiddenRowKeys)
      payload.hiddenHeaderFieldKeys = Array.from(hiddenHeaderFieldKeys)
      payload.salesDate = salesDate
      payload.readOnly = isReadOnly
      payload.confirmState = draftAccess.confirmState
      payload.canExportExcel = draftAccess.canExportExcel
      payload.canExportCsv = draftAccess.canExportCsv
      payload.canExportDbExcel = draftAccess.canExportDbExcel

      sessionStorage.setItem(payloadKey, JSON.stringify(payload))
    } catch (e) {
      console.error('Failed to save to sessionStorage:', e)
    }
  }, [
    searchParams,
    selectedNyukaNumbers,
    inventoryData,
    additionalRows,
    hiddenColumnKeys,
    distributionManager,
    columnManager,
    rowOrder,
    hiddenRowKeys,
    hiddenHeaderFieldKeys,
    salesDate,
    isReadOnly,
    draftAccess
  ])

  const applyDraftDetailFromServer = useCallback(
    (draftDetail, payloadKey = null) => {
      setDraftAccess({
        editable: draftDetail.editable !== false,
        confirmState: draftDetail.confirm_state || 'draft',
        canExportExcel: Boolean(draftDetail.can_export_excel),
        canExportCsv: Boolean(draftDetail.can_export_csv),
        canExportDbExcel: Boolean(draftDetail.can_export_db_excel)
      })
      setConfirmedLocked(draftDetail.confirm_state === 'confirmed')

      const apiData = draftDetail.output_data
      const name = getSafeProductName(apiData.result.data)
      const saleMan = draftDetail.name
      const payload = createEditPayload(apiData)

      let draftSellerCode = ''
      let draftCustomers = null
      try {
        const inputData =
          typeof draftDetail.input_data === 'string'
            ? JSON.parse(draftDetail.input_data)
            : draftDetail.input_data
        const dataSection = inputData?.data || {}
        if (Array.isArray(dataSection?.customers)) {
          draftCustomers = dataSection.customers
        }
        draftSellerCode =
          dataSection?.seller_code ||
          dataSection?.seller_cd ||
          inputData?.seller_code ||
          inputData?.seller_cd ||
          ''
      } catch (e) {
        draftSellerCode = ''
      }
      if (draftSellerCode) setSellerCode(draftSellerCode)
      if (draftCustomers) setOrderCustomers(draftCustomers)
      setProductName(name || '')
      setSaleMan(saleMan || '')

      if (!payload) return

      setSelectedNyukaNumbers(payload.selectedNyukaNumbers || {})
      setInventoryData(payload.inventoryData || {})
      setAdditionalRows(payload.additionalRows || [])
      distributionManager.setData({})
      distributionManager.draftCells = {}
      distributionManager.resetCells.clear()
      // Server rows are authoritative. syncItems would keep the allocations the
      // manager loaded earlier (and the edits are cleared above), so a focus
      // refresh after saving showed the pre-save quantities again.
      isFirstLoadRef.current = true
      sessionHoldsSavedDraftRef.current = false

      if (payload.columnConfig) {
        columnManager.importConfig(payload.columnConfig)
      } else {
        columnManager.importConfig(new ColumnManager().exportConfig())
      }

      setHiddenColumnKeys(new Set(payload.hiddenColumnKeys || []))
      setRowOrder(payload.rowOrder || [])
      setHiddenRowKeys(new Set(payload.hiddenRowKeys || []))
      setHiddenHeaderFieldKeys(new Set(payload.hiddenHeaderFieldKeys || []))

      if (apiData.result?.orders) setOrders(apiData.result.orders)

      let calculatedSalesDate = ''
      if (draftDetail?.created_at) {
        calculatedSalesDate = formatDatePlusOne(draftDetail.created_at)
        setSalesDate(calculatedSalesDate)
      }

      if (payloadKey) {
        sessionStorage.setItem(
          payloadKey,
          JSON.stringify({
            ...payload,
            productName: name,
            saleMan,
            orders: apiData.result?.orders ?? [],
            customers: draftCustomers ?? [],
            seller_code: draftSellerCode,
            salesDate: calculatedSalesDate,
            readOnly: draftDetail.editable === false,
            confirmState: draftDetail.confirm_state || 'draft',
            canExportExcel: Boolean(draftDetail.can_export_excel),
            canExportCsv: Boolean(draftDetail.can_export_csv),
            canExportDbExcel: Boolean(draftDetail.can_export_db_excel)
          })
        )
      }

      const hasUi = !!(
        apiData &&
        apiData.result &&
        apiData.result.data &&
        apiData.result.data.ui_metadata &&
        Object.keys(apiData.result.data.ui_metadata).length > 0
      )
      setInitialUiMetadataPresent(hasUi)
      setHasUnsavedChanges(false)
      setForceUpdate((v) => v + 1)
    },
    [columnManager, distributionManager]
  )

  useEffect(() => {
    if (isInitialLoadComplete) {
      saveSessionState()
    }
  }, [saveSessionState, isInitialLoadComplete])

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        const payloadKey = searchParams.get('payloadKey')
        const draftId = searchParams.get('draftId') || searchParams.get('id')
        const forceRefresh = searchParams.get('forceRefresh') === '1'

        if (payloadKey && !forceRefresh) {
          try {
            const stored = sessionStorage.getItem(payloadKey)
            const payload = stored ? JSON.parse(stored) : null
            if (payload && typeof payload === 'object') {
              if (payload.selectedNyukaNumbers)
                setSelectedNyukaNumbers(payload.selectedNyukaNumbers)
              if (payload.inventoryData) setInventoryData(payload.inventoryData)
              if (payload.distributionData)
                distributionManager.setData(payload.distributionData)
              if (payload.additionalRows)
                setAdditionalRows(payload.additionalRows)

              if (payload.columnConfig) {
                columnManager.importConfig(payload.columnConfig)
              }
              if (payload.hiddenColumnKeys) {
                setHiddenColumnKeys(new Set(payload.hiddenColumnKeys))
              }
              if (payload.rowOrder) {
                setRowOrder(payload.rowOrder)
              }
              if (payload.hiddenRowKeys) {
                setHiddenRowKeys(new Set(payload.hiddenRowKeys))
              }
              if (payload.hiddenHeaderFieldKeys) {
                setHiddenHeaderFieldKeys(new Set(payload.hiddenHeaderFieldKeys))
              }

              if (payload.orders) setOrders(payload.orders)
              if (Array.isArray(payload.customers)) {
                setOrderCustomers(payload.customers)
              }
              if (payload.seller_code) setSellerCode(payload.seller_code)
              if (payload.productName !== undefined) {
                setProductName(payload.productName || '')
              }
              if (payload.saleMan !== undefined) {
                setSaleMan(payload.saleMan || '')
              }
              if (payload.salesDate) {
                setSalesDate(payload.salesDate)
              } else if (payload.created_at) {
                setSalesDate(formatDatePlusOne(payload.created_at))
              } else if (payload.createdAt) {
                setSalesDate(formatDatePlusOne(payload.createdAt))
              }

              setDraftAccess({
                editable:
                  payload.readOnly === true
                    ? false
                    : payload.editable !== undefined
                      ? Boolean(payload.editable)
                      : payload.confirmState !== 'confirmed',
                confirmState: payload.confirmState || 'draft',
                canExportExcel: Boolean(payload.canExportExcel),
                canExportCsv: Boolean(payload.canExportCsv),
                canExportDbExcel: Boolean(payload.canExportDbExcel)
              })
              setConfirmedLocked(
                payload.readOnly === true ||
                  payload.confirmState === 'confirmed'
              )

              // detect ui_metadata presence from stored payload (if any)
              try {
                const storedRaw = sessionStorage.getItem(payloadKey)
                const storedObj = storedRaw ? JSON.parse(storedRaw) : null
                // createEditPayload spreads ui_metadata fields at top level;
                // saved draft payload may therefore have columnConfig/top-level config directly.
                const hasUi = !!(
                  storedObj &&
                  (Object.prototype.hasOwnProperty.call(
                    storedObj,
                    'ui_metadata'
                  ) ||
                    Object.prototype.hasOwnProperty.call(
                      storedObj,
                      'columnConfig'
                    ))
                )
                setInitialUiMetadataPresent(hasUi)
              } catch (e) {
                // ignore
              }
              return
            }
          } catch (e) {
            console.error('Failed to load from sessionStorage:', e)
          }
        }

        if (draftId) {
          const draftDetail = await getDraftById(draftId)
          applyDraftDetailFromServer(draftDetail, payloadKey)
        }
      } catch (e) {
        console.error('Failed to load draft data:', e)
      } finally {
        setIsLoading(false)
        setIsInitialLoadComplete(true)
      }
    }

    loadData()
  }, [
    searchParams,
    distributionManager,
    columnManager,
    applyDraftDetailFromServer
  ])

  const lastFocusRefreshRef = useRef(0)
  // Read after the focus refresh request resolves; the closure's values are
  // from when the request started.
  const focusRefreshStateRef = useRef({ hasUnsavedChanges, isLoading })
  focusRefreshStateRef.current = { hasUnsavedChanges, isLoading }

  useEffect(() => {
    const draftId = searchParams.get('draftId') || searchParams.get('id')
    if (!draftId) return

    const refreshFromServerOnFocus = async () => {
      if (document.visibilityState !== 'visible') return
      if (hasUnsavedChanges || isLoading || isReadOnly) return
      // Applying the server rows rebuilds the manager and drops the undo
      // history, which must stay available after 保存.
      if (distributionManager.getLog().length > 1) return

      const now = Date.now()
      if (now - lastFocusRefreshRef.current < 1000) return
      lastFocusRefreshRef.current = now

      const logStamp = () =>
        `${distributionManager.getLog().length}:${distributionManager.nextLogIndex}`
      const logBefore = logStamp()
      const savedVersionBefore = savedDraftVersionRef.current

      try {
        const selectedNyukabango = Object.values(selectedNyukaNumbers)
          .flat()
          .filter(Boolean)
        const draftDetail = await getDraftById(
          draftId,
          selectedNyukabango.length ? selectedNyukabango : undefined
        )
        // The user can start editing (or save) while this request is in
        // flight. Applying the response then wipes those edits, and a later
        // save stores the server's old quantities.
        const current = focusRefreshStateRef.current
        if (
          current.hasUnsavedChanges ||
          current.isLoading ||
          logStamp() !== logBefore ||
          Object.keys(distributionManager.draftCells).length > 0 ||
          savedDraftVersionRef.current !== savedVersionBefore
        ) {
          return
        }
        applyDraftDetailFromServer(draftDetail)
      } catch (error) {
        console.error('Failed to refresh draft on focus:', error)
      }
    }

    window.addEventListener('focus', refreshFromServerOnFocus)
    document.addEventListener('visibilitychange', refreshFromServerOnFocus)

    return () => {
      window.removeEventListener('focus', refreshFromServerOnFocus)
      document.removeEventListener('visibilitychange', refreshFromServerOnFocus)
    }
  }, [
    searchParams,
    selectedNyukaNumbers,
    hasUnsavedChanges,
    isLoading,
    isReadOnly,
    applyDraftDetailFromServer,
    distributionManager
  ])

  useEffect(() => {
    if (autoSelectedAllRef.current) return
    if (!inventoryData || typeof inventoryData !== 'object') return

    const hasAnySelection =
      selectedNyukaNumbers &&
      typeof selectedNyukaNumbers === 'object' &&
      Object.values(selectedNyukaNumbers).some(
        (arr) => Array.isArray(arr) && arr.length > 0
      )

    const invKeys = Object.keys(inventoryData || {})
    if (invKeys.length === 0) return

    if (!hasAnySelection) {
      const allSelected = {}
      invKeys.forEach((hinmeiCode) => {
        const rows = Array.isArray(inventoryData[hinmeiCode])
          ? inventoryData[hinmeiCode]
          : []
        allSelected[hinmeiCode] = rows
          .map((r) => r?.niukabango)
          .filter((v) => v !== null && v !== undefined && String(v) !== '')
          .map((v) => String(v))
      })
      autoSelectedAllRef.current = true
      setSelectedNyukaNumbers(allSelected)
    }
  }, [inventoryData, selectedNyukaNumbers])

  const baseItems = useMemo(() => {
    const selectedItems = []

    Object.entries(selectedNyukaNumbers).forEach(
      ([hinmeiCode, niukabangos]) => {
        niukabangos.forEach((niukabango) => {
          const niukaItem = inventoryData[hinmeiCode]?.find(
            (item) => String(item.niukabango) === String(niukabango)
          )

          if (!niukaItem) return

          if (Array.isArray(niukaItem.items) && niukaItem.items.length > 0) {
            niukaItem.items.forEach((detail, itemIndex) => {
              if (!detail || typeof detail !== 'object') return

              const uniqueNiukabango =
                niukaItem.items.length > 1
                  ? `${niukaItem.niukabango}__${itemIndex}`
                  : niukaItem.niukabango

              const priceGroup =
                Array.isArray(detail.price_groups) &&
                detail.price_groups.length > 0
                  ? detail.price_groups
                  : [
                      {
                        input_price: detail.input_price,
                        output_price: detail.output_price,
                        profit: detail.profit,
                        distribution_result: detail.distribution_result || [],
                        _priceId: `p0`
                      }
                    ]

              selectedItems.push({
                ...detail,
                hinmeiCode: detail.hinmeiCode ?? hinmeiCode,
                niukabango: uniqueNiukabango,
                _originalNiukabango: niukaItem.niukabango,
                _itemIndex: itemIndex,
                price_groups: priceGroup
              })
            })
          } else {
            const priceGroup =
              Array.isArray(niukaItem.price_groups) &&
              niukaItem.price_groups.length > 0
                ? niukaItem.price_groups
                : [
                    {
                      input_price: niukaItem.input_price,
                      output_price: niukaItem.output_price,
                      profit: niukaItem.profit,
                      distribution_result: niukaItem.distribution_result || [],
                      _priceId: `p0`
                    }
                  ]

            selectedItems.push({
              ...niukaItem,
              hinmeiCode: niukaItem.hinmeiCode ?? hinmeiCode,
              niukabango: niukaItem.niukabango,
              _originalNiukabango: niukaItem.niukabango,
              _itemIndex: 0,
              price_groups: priceGroup
            })
          }
        })
      }
    )
    return selectedItems
  }, [selectedNyukaNumbers, inventoryData])

  const getRowKey = useCallback(
    (item) => `${item.hinmeiCode}::${item.niukabango}`,
    []
  )

  const getSelectedItems = useCallback(() => {
    const compareText = (a, b) =>
      String(a ?? '').localeCompare(String(b ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      })

    const allItems = [...baseItems, ...additionalRows]

    const flattenedItems = []
    allItems.forEach((item) => {
      const priceGroups =
        Array.isArray(item.price_groups) && item.price_groups.length > 0
          ? item.price_groups
          : [
              {
                input_price: item.input_price,
                output_price: item.output_price,
                profit: item.profit,
                distribution_result: item.distribution_result || [],
                _priceId: 'p0'
              }
            ]

      priceGroups.forEach((group, pIdx) => {
        const rowNiukabango = `${item.niukabango}__p_${group._priceId}`
        const mergedItem = {
          ...item,
          ...group,
          _parentItem: item,
          _parentNiukabango: item.niukabango,
          _priceGroupIndex: pIdx,
          _priceId: group._priceId,
          niukabango: rowNiukabango
        }

        FIXED_FIELDS.forEach((field) => {
          const fixedRowKey = rowNiukabango
          mergedItem[field] = distributionManager.getFixedField(
            item.hinmeiCode,
            fixedRowKey,
            field,
            mergedItem[field]
          )
        })

        flattenedItems.push(mergedItem)
      })
    })

    const sortedItems = flattenedItems.sort((a, b) => {
      const keyA = getRowKey(a)
      const keyB = getRowKey(b)

      const idxA = rowOrder.indexOf(keyA)
      const idxB = rowOrder.indexOf(keyB)

      if (idxA !== -1 && idxB !== -1) {
        return idxA - idxB
      }
      if (idxA !== -1) return -1
      if (idxB !== -1) return 1

      const productA = String(a?.hinmeiCode ?? a?.product_code ?? '')
      const productB = String(b?.hinmeiCode ?? b?.product_code ?? '')
      const productCmp = compareText(productA, productB)

      if (productCmp !== 0) return productCmp

      const nyukaA = String(
        a?._originalNiukabango ?? a?._parentNiukabango ?? a?.niukabango ?? ''
      ).split('__')[0]
      const nyukaB = String(
        b?._originalNiukabango ?? b?._parentNiukabango ?? b?.niukabango ?? ''
      ).split('__')[0]
      const nyukaCmp = compareText(nyukaA, nyukaB)
      if (nyukaCmp !== 0) return nyukaCmp

      const shippingCmp = compareText(a?.shipping_mark, b?.shipping_mark)
      if (shippingCmp !== 0) return shippingCmp

      const gradeCmp = compareText(a?.grade, b?.grade)
      if (gradeCmp !== 0) return gradeCmp

      const sizeCmp = compareText(a?.size, b?.size)
      if (sizeCmp !== 0) return sizeCmp

      const packageCmp = compareText(a?.package, b?.package)
      if (packageCmp !== 0) return packageCmp

      const sanchiCmp = compareText(a?.sanchi_code, b?.sanchi_code)
      if (sanchiCmp !== 0) return sanchiCmp

      const shipperCmp = compareText(a?.shipper_code, b?.shipper_code)
      if (shipperCmp !== 0) return shipperCmp

      const itemIndexCmp =
        Number(a?._itemIndex ?? 0) - Number(b?._itemIndex ?? 0)
      if (itemIndexCmp !== 0) return itemIndexCmp

      return Number(a?._priceGroupIndex ?? 0) - Number(b?._priceGroupIndex ?? 0)
    })

    return sortedItems
  }, [baseItems, additionalRows, rowOrder, getRowKey, distributionManager])

  // DistributionManager mutates fixed fields in place. The UI revision is an
  // intentional cache key so the latest values are read after edits/undo/redo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allSelectedItems = useMemo(() => getSelectedItems(), [
    getSelectedItems,
    forceUpdate
  ])

  const farmFilterOptions = useMemo(() => {
    const farmsByValue = new Map()

    allSelectedItems.forEach((item) => {
      const code = String(item?.sanchi_code ?? '').trim()
      const name = String(item?.shipper_code ?? '').trim()
      if (!code && !name) return

      // sanchi_code is the stable farm identifier. Rows without a code fall
      // back to their name so they can still be filtered independently.
      const value = code || `__no_sanchi_code__:${name}`
      if (!farmsByValue.has(value)) {
        farmsByValue.set(value, { value, code, name })
      }
    })

    const nameCounts = new Map()
    farmsByValue.forEach(({ name }) => {
      const displayName = name || '名称未設定'
      nameCounts.set(displayName, (nameCounts.get(displayName) || 0) + 1)
    })

    return Array.from(farmsByValue.values())
      .map(({ value, code, name }) => {
        const displayName = name || '名称未設定'
        const needsCode = !name || nameCounts.get(displayName) > 1
        return {
          value,
          label: needsCode && code ? `${displayName} (${code})` : displayName
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'ja'))
  }, [allSelectedItems])

  const gradeFilterOptions = useMemo(
    () => buildValueFilterOptions(allSelectedItems, 'grade'),
    [allSelectedItems]
  )
  const sizeFilterOptions = useMemo(
    () => buildValueFilterOptions(allSelectedItems, 'size'),
    [allSelectedItems]
  )

  useEffect(() => {
    const availableValues = new Set(farmFilterOptions.map((option) => option.value))
    setFarmFilterValues((currentValues) => {
      const validValues = currentValues.filter((value) => availableValues.has(value))
      return validValues.length === currentValues.length ? currentValues : validValues
    })
  }, [farmFilterOptions])

  useEffect(() => {
    setGradeFilterValues((current) => {
      const next = pruneUnavailableFilterValues(current, gradeFilterOptions)
      return next.length === current.length ? current : next
    })
  }, [gradeFilterOptions])

  useEffect(() => {
    setSizeFilterValues((current) => {
      const next = pruneUnavailableFilterValues(current, sizeFilterOptions)
      return next.length === current.length ? current : next
    })
  }, [sizeFilterOptions])

  const visibleSelectedItems = useMemo(() => {
    return allSelectedItems.filter((item) => {
      const code = String(item?.sanchi_code ?? '').trim()
      const name = String(item?.shipper_code ?? '').trim()
      const value = code || `__no_sanchi_code__:${name}`
      const matchesFarm =
        farmFilterValues.length === 0 || farmFilterValues.includes(value)
      const grade = normalizeValueFilterValue(item?.grade)
      const size = normalizeValueFilterValue(item?.size)
      const matchesGrade =
        gradeFilterValues.length === 0 || gradeFilterValues.includes(grade)
      const matchesSize =
        sizeFilterValues.length === 0 || sizeFilterValues.includes(size)
      return matchesFarm && matchesGrade && matchesSize
    })
  }, [
    allSelectedItems,
    farmFilterValues,
    gradeFilterValues,
    sizeFilterValues
  ])

  const scheduleFilterChange = useCallback(
    (field, values) => {
      const requestId = filterRequestRef.current + 1
      filterRequestRef.current = requestId

      const pendingFilter = {
        requestId,
        startedAt: Date.now(),
        grade: field === 'grade' ? values : gradeFilterValues,
        size: field === 'size' ? values : sizeFilterValues
      }
      pendingFilterRef.current = pendingFilter

      // Commit the overlay first. Applying the filter in the next task gives
      // the browser a paint opportunity before the table's expensive render.
      flushSync(() => setIsFilterLoading(true))
      window.setTimeout(() => {
        if (filterRequestRef.current !== requestId) return
        startTransition(() => {
          if (field === 'grade') setGradeFilterValues(values)
          else setSizeFilterValues(values)
        })
      }, 0)
    },
    [gradeFilterValues, sizeFilterValues]
  )

  const handleGradeFilterChange = useCallback(
    (values) => scheduleFilterChange('grade', values),
    [scheduleFilterChange]
  )

  const handleSizeFilterChange = useCallback(
    (values) => scheduleFilterChange('size', values),
    [scheduleFilterChange]
  )

  useEffect(() => {
    if (!isFilterLoading) return undefined
    const pendingFilter = pendingFilterRef.current
    if (!pendingFilter) return undefined

    const sameValues = (left, right) =>
      left.length === right.length && left.every((value, index) => value === right[index])

    if (
      !sameValues(gradeFilterValues, pendingFilter.grade) ||
      !sameValues(sizeFilterValues, pendingFilter.size)
    ) {
      return undefined
    }

    const requestId = pendingFilter.requestId
    const remainingLoadingTime = Math.max(
      0,
      1000 - (Date.now() - pendingFilter.startedAt)
    )
    const timer = window.setTimeout(() => {
      if (filterRequestRef.current !== requestId) return
      if (pendingFilterRef.current?.requestId !== requestId) return
      pendingFilterRef.current = null
      setIsFilterLoading(false)
    }, remainingLoadingTime)
    return () => window.clearTimeout(timer)
  }, [
    gradeFilterValues,
    sizeFilterValues,
    visibleSelectedItems,
    isFilterLoading
  ])

  const flattenItems = useCallback(
    (items) => {
      const flat = []
      items.forEach((item) => {
        const priceGroup = item.price_groups || []
        priceGroup.forEach((p) => {
          const rowNiukabango = `${item.niukabango}__p_${p._priceId}`
          const flattenedItem = {
            ...item,
            ...p,
            niukabango: rowNiukabango,
            _parentNiukabango: item.niukabango
          }

          FIXED_FIELDS.forEach((field) => {
            const fixedRowKey = rowNiukabango

            flattenedItem[field] = distributionManager.getFixedField(
              item.hinmeiCode,
              fixedRowKey,
              field,
              flattenedItem[field]
            )
          })

          flat.push(flattenedItem)
        })
      })
      return flat
    },
    [distributionManager, getFixedRowKey]
  )

  const allItems = useMemo(
    () => [...baseItems, ...additionalRows],
    [baseItems, additionalRows]
  )

  useMemo(() => {
    if (allItems.length === 0) return
    if (isFirstLoadRef.current) {
      // Rebuild before the table renders: cells take originalValue from the
      // manager during this render and are memoized, so rebuilding later in an
      // effect left them showing the previous load (e.g. right after saving)
      // until something else re-rendered them.
      distributionManager.setOriginalData(flattenItems(allItems))
    } else {
      distributionManager.syncItems(flattenItems(allItems))
    }
  }, [allItems, distributionManager, flattenItems])

  const shouldShowCustomerColumn = useCallback(
    (columnKey, distribution) => {
      const allocatedQuantity = Number(
        distribution?.delivery_quantity ?? distribution?.order_quantity ?? 0
      )
      if (allocatedQuantity > 0) return true

      if (!orders || orders.length === 0) return true

      const { customerCode, branchCode } =
        ColumnManager.parseColumnKey(columnKey)

      return orders.some((entry) => {
        const ordersList = entry.orders_list || []
        return ordersList.some(
          (o) =>
            String(o.customer_code ?? o.CUSTOMER_CODE ?? '') ===
              String(customerCode) &&
            String(o.branch_code ?? o.BRANCH_CODE ?? '') === String(branchCode)
        )
      })
    },
    [orders]
  )

  const hasOriginalOrderQuantity = useCallback(
    (columnKey) => {
      const { customerCode, branchCode, index } =
        ColumnManager.parseColumnKey(columnKey)
      const targetCode = String(customerCode ?? '')
      const targetBranch = String(branchCode ?? '')

      for (const item of allItems) {
        const priceGroups = Array.isArray(item.price_groups)
          ? item.price_groups
          : []
        for (const group of priceGroups) {
          const distResults = Array.isArray(group.distribution_result)
            ? group.distribution_result
            : []
          // Only the result at this column's occurrence index belongs to it;
          // a duplicate column of the same pair has no original order.
          const pairResults = distResults.filter(
            (d) =>
              String(d.customer_code ?? '') === targetCode &&
              String(d.branch_code ?? '') === targetBranch
          )
          const qty = pairResults[index]?.original_order_quantity
          if (qty !== null && qty !== undefined && String(qty).trim() !== '') {
            return true
          }
        }
      }
      return false
    },
    [allItems]
  )

  useEffect(() => {
    if (isFirstLoadRef.current && allItems.length > 0) {
      if (rowOrder.length === 0) {
        const initialSorted = getSelectedItems()
        setRowOrder(initialSorted.map((item) => getRowKey(item)))
      }

      // distributionManager was rebuilt from these rows during render (above).
      // setOriginalData creates the immutable baseline log internally, but does
      // not emit a log event. Keep the toolbar state in sync so Undo is disabled
      // until the user makes their first change.
      setChangeLog([...distributionManager.getLog()])
      setCurrentLogIndex(distributionManager.nextLogIndex - 1)
      isFirstLoadRef.current = false
      // Cells keep their own displayed value; resync them with the rebuilt data.
      eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
    }

    if (allItems.length === 0) return

    const columnDefs = []
    const seenKeys = new Set()
    const reserveByKey = new Map()

    // React rows keep the allocations they were loaded with; branch renames and
    // column deletions only live in distributionManager. Deriving columns from
    // the stale React rows re-adds the renamed source keys (e.g. 3166::NL::1)
    // as extra columns whenever this effect re-runs, such as after saving.
    distributionManager.originalItems.forEach((item) => {
      const pairCount = {}

      ;(item.distribution_result || []).forEach((d) => {
        const code = String(d.customer_code ?? '')
        if (!code || code === 'price' || code === 'id') return

        const branchCode = String(d.branch_code ?? '')
        const pairKey = `${code}::${branchCode}`
        const idx = pairCount[pairKey] ?? 0
        pairCount[pairKey] = idx + 1

        // Pads only keep later occurrence indexes stable; they are not columns.
        if (d._columnPlaceholder) return

        const columnKey = ColumnManager.buildColumnKey(code, branchCode, idx)
        if (!shouldShowCustomerColumn(columnKey, d)) return

        const existing = reserveByKey.get(columnKey)
        if (!existing) {
          reserveByKey.set(columnKey, {
            reserve: d.reserve || 0,
            location: d.location || ''
          })
        } else {
          if ((!existing.reserve || existing.reserve === 0) && d.reserve > 0) {
            existing.reserve = d.reserve
          }
          if (!existing.location && d.location) {
            existing.location = d.location
          }
        }

        if (!seenKeys.has(columnKey)) {
          seenKeys.add(columnKey)
          columnDefs.push({
            columnKey,
            label: code
          })
        }
      })
    })

    columnManager.bulkAddCustomerColumns(columnDefs)

    reserveByKey.forEach(({ reserve, location }, columnKey) => {
      const fields = columnManager.customerHeaderFields?.get(columnKey)
      if (!fields) return
      if (fields.torioiki === '' && reserve === 1) fields.torioiki = '●'
      if (fields.torioiki === '' && reserve === 2) fields.torioiki = '＋1日'
      if (fields.torioiki === '' && reserve === 3) fields.torioiki = '＋2日'
      if (fields.basho === '' && location) fields.basho = location
    })

    // If the original API payload did NOT include ui_metadata, enforce a deterministic
    // ordering of customer columns once during initialization only.
    // This is treated as internal initialization, not as a user reorder action.
    if (!initialUiMetadataPresent && !hasAppliedInitialColumnSortRef.current) {
      try {
        const customerCols = columnManager.getCustomerColumns()
        const groups = { 'has-order': [], 'original-order': [], deletable: [] }

        customerCols.forEach((col) => {
          const type = getColumnType(
            col.customerCode,
            orders,
            hasOriginalOrderQuantity
          )
          groups[type] = groups[type] || []
          groups[type].push(col)
        })

        const compareCols = (a, b) => {
          const pa = ColumnManager.parseColumnKey(a.customerCode)
          const pb = ColumnManager.parseColumnKey(b.customerCode)
          const cmp = String(pa.customerCode ?? '').localeCompare(
            String(pb.customerCode ?? ''),
            undefined,
            { numeric: true, sensitivity: 'base' }
          )
          if (cmp !== 0) return cmp
          return String(pa.branchCode ?? '').localeCompare(
            String(pb.branchCode ?? ''),
            undefined,
            { numeric: true, sensitivity: 'base' }
          )
        }

        groups['has-order'].sort(compareCols)
        groups['original-order'].sort(compareCols)
        groups['deletable'].sort(compareCols)

        const sortedCustomerIds = [
          ...groups['has-order'],
          ...groups['original-order'],
          ...groups['deletable']
        ].map((c) => c.id)

        // Build new full order preserving non-customer columns' relative order
        const currentCols = columnManager.getColumns()
        const newOrder = []
        let ci = 0
        currentCols.forEach((c) => {
          if (c.type === 'customer' || c.type === 'black-circle') {
            const nextId = sortedCustomerIds[ci++] || c.id
            newOrder.push(nextId)
          } else {
            newOrder.push(c.id)
          }
        })

        // Only apply if lengths match to avoid accidental mis-ordering
        if (newOrder.length === columnManager.getColumnCount()) {
          columnManager.reorderColumns(newOrder, { emitEvent: false })
        }
      } catch (e) {
        // If anything goes wrong, skip reordering to avoid breaking the UI
        console.warn(
          'Failed to apply deterministic customer column ordering',
          e
        )
      } finally {
        hasAppliedInitialColumnSortRef.current = true
      }
    }
  }, [
    baseItems,
    additionalRows,
    columnManager,
    distributionManager,
    getRowKey,
    getSelectedItems,
    shouldShowCustomerColumn,
    rowOrder,
    flattenItems,
    initialUiMetadataPresent,
    orders,
    hasOriginalOrderQuantity
  ])

  const canSave = useMemo(() => {
    if (isReadOnly) return false
    const selectedItems = getSelectedItems()
    return distributionManager.canSave(selectedItems, columnManager)
  }, [isReadOnly, getSelectedItems, distributionManager, columnManager, forceUpdate])

  useEffect(() => {
    const unsubscribers = [
      eventBus.on(EVENTS.CELL_VALUE_CHANGED, () => {
        if (isReadOnly) return
        setForceUpdate((v) => v + 1)
        setHasUnsavedChanges(true)
      }),
      eventBus.on(EVENTS.DISTRIBUTION_CHANGED, () => {
        if (isReadOnly) return
        setForceUpdate((v) => v + 1)
        setHasUnsavedChanges(true)
      }),
      eventBus.on(EVENTS.CUSTOMER_HEADER_CHANGED, () => {
        if (isReadOnly) return
        setForceUpdate((v) => v + 1)
        setHasUnsavedChanges(true)
      }),
      eventBus.on(EVENTS.LOG_ENTRY_ADDED, () => {
        if (isReadOnly) return
        setChangeLog([...distributionManager.getLog()])
        setHasUnsavedChanges(true)
      }),
      eventBus.on(EVENTS.LOG_CLEARED, () => setChangeLog([])),
      eventBus.on(EVENTS.COLUMN_ADDED, () => {
        if (isReadOnly) return
        setForceUpdate((v) => v + 1)
        setHasUnsavedChanges(true)
      }),
      eventBus.on(EVENTS.COLUMN_REMOVED, () => {
        if (isReadOnly) return
        setForceUpdate((v) => v + 1)
        setHasUnsavedChanges(true)
      })
    ]
    return () => unsubscribers.forEach((unsub) => unsub())
  }, [distributionManager, isReadOnly])

  useEffect(() => {
    return eventBus.on(EVENTS.VALIDATION_ERROR, (data) => {
      showToast(
        data?.message || '入力内容を確認してください。',
        data?.type || 'error'
      )
    })
  }, [showToast])

  const handleAddNewRow = useCallback(
    (newItem) => {
      if (isReadOnly) return
      const itemWithPriceGroup = normalizeItemForUI({
        ...newItem,
        price_groups:
          Array.isArray(newItem.price_groups) && newItem.price_groups.length > 0
            ? newItem.price_groups
            : [
                {
                  input_price: newItem.input_price,
                  output_price: newItem.output_price,
                  profit: newItem.profit,
                  distribution_result: newItem.distribution_result || [],
                  _priceId: `p_new_${Date.now()}`
                }
              ]
      })
      setAdditionalRows((prev) => [...prev, itemWithPriceGroup])
      setForceUpdate((v) => v + 1)
    },
    [isReadOnly]
  )

  const handleSplitDialogClose = useCallback(() => {
    setSplitDialogState({
      open: false,
      item: null,
      priceId: null,
      maxQuantity: null,
      quantity: '',
      error: ''
    })
  }, [])

  const handleAddPrice = useCallback(
    (item, priceId) => {
      if (isReadOnly) return
      const parseNum = (val) => {
        const s = String(val ?? '')
          .trim()
          .replace(/,/g, '')
        const n = Number(s)
        return Number.isFinite(n) ? n : 0
      }

      const parentNiukabango =
        item._parentNiukabango ??
        item._originalNiukabango ??
        String(item.niukabango).split('__')[0]
      const groupNiukabango = `${parentNiukabango}__p_${priceId}`
      const hinmeiCode = item.hinmeiCode

      const editedQuantity =
        distributionManager.getFixedField(
          hinmeiCode,
          groupNiukabango,
          'quantity',
          undefined
        ) ?? item.quantity

      const maxQuantity = parseNum(editedQuantity)

      setSplitDialogState({
        open: true,
        item,
        priceId,
        maxQuantity,
        quantity: '',
        error: ''
      })
    },
    [distributionManager, isReadOnly]
  )

  const createSplitGroup = useCallback(
    (sourceGroup, parentItem, splitQuantity, newPriceId) => {
      const parentNiukabango =
        parentItem._parentNiukabango ??
        parentItem._originalNiukabango ??
        String(parentItem.niukabango).split('__')[0]
      const groupNiukabango = `${parentNiukabango}__p_${sourceGroup._priceId}`
      const hinmeiCode = parentItem.hinmeiCode

      const getLatest = (field, fallbackGroup, fallbackParent) => {
        // 1. Check if there is an edit on this specific price group in distributionManager
        const groupVal = distributionManager.getFixedField(
          hinmeiCode,
          groupNiukabango,
          field,
          undefined
        )
        if (groupVal !== undefined && groupVal !== null && groupVal !== '')
          return groupVal

        // 2. Check the price group's own loaded value
        if (
          fallbackGroup !== undefined &&
          fallbackGroup !== null &&
          fallbackGroup !== ''
        )
          return fallbackGroup

        // 3. Check if there is an edit on the parent item in distributionManager
        const parentVal = distributionManager.getFixedField(
          hinmeiCode,
          parentNiukabango,
          field,
          undefined
        )
        if (parentVal !== undefined && parentVal !== null && parentVal !== '')
          return parentVal

        // 4. Fall back to the parent's original value
        return fallbackParent ?? ''
      }

      const getLatestEdit = (field) => {
        const groupVal = distributionManager.getFixedField(
          hinmeiCode,
          groupNiukabango,
          field,
          undefined
        )
        if (groupVal !== undefined && groupVal !== null && groupVal !== '')
          return groupVal

        const parentVal = distributionManager.getFixedField(
          hinmeiCode,
          parentNiukabango,
          field,
          undefined
        )
        if (parentVal !== undefined && parentVal !== null && parentVal !== '')
          return parentVal

        return undefined
      }

      const currentQuantity = Number(
        getLatestEdit('quantity') ??
          sourceGroup.quantity ??
          parentItem.quantity ??
          0
      )

      const remainingQuantity = currentQuantity - splitQuantity

      const getMergedValue = (field, fallbackValue) => {
        const latestVal = getLatest(
          field,
          sourceGroup[field],
          parentItem[field]
        )
        return latestVal ?? fallbackValue
      }

      const baseMeta = {
        shipper_code:
          getMergedValue('shipper_code', sourceGroup.shipper_code) ?? '',
        shipper_name:
          getMergedValue('shipper_name', sourceGroup.shipper_name) ?? '',
        hinmeiCode: getMergedValue('hinmeiCode', sourceGroup.hinmeiCode) ?? '',
        hinmeiName: getMergedValue('hinmeiName', sourceGroup.hinmeiName) ?? '',
        product_code:
          getMergedValue('product_code', sourceGroup.product_code) ?? '',
        product_name:
          getMergedValue('product_name', sourceGroup.product_name) ?? '',
        shipping_mark:
          getMergedValue('shipping_mark', sourceGroup.shipping_mark) ?? '',
        grade: getMergedValue('grade', sourceGroup.grade) ?? '',
        size: getMergedValue('size', sourceGroup.size) ?? '',
        package: getMergedValue('package', sourceGroup.package) ?? '',
        weight: getMergedValue('weight', sourceGroup.weight) ?? '',
        quantity_unit:
          getMergedValue('quantity_unit', sourceGroup.quantity_unit) ?? 0,
        remain_quantity:
          getMergedValue('remain_quantity', sourceGroup.remain_quantity) ?? 0,
        package_quantity:
          getMergedValue('package_quantity', sourceGroup.package_quantity) ?? 0,
        listing_quantity:
          getMergedValue('listing_quantity', sourceGroup.listing_quantity) ?? 0
      }

      const latestInputPrice =
        getMergedValue('input_price', sourceGroup.input_price) ?? ''
      const latestOutputPrice =
        getMergedValue('output_price', sourceGroup.output_price) ?? ''

      // ✅ Important: updatedGroup preserves sourceGroup's existing values and updates only necessary fields
      const updatedGroup = {
        ...sourceGroup,
        input_price: latestInputPrice,
        output_price: latestOutputPrice,
        total_quantity: remainingQuantity,
        quantity: remainingQuantity,
        ...baseMeta
      }

      // ✅ newGroup gets the metadata from baseMeta but NOT values from sourceGroup
      // This ensures new group has fresh values, not copies of split source
      const newGroup = {
        input_price: latestInputPrice,
        output_price: '',
        profit: '',
        distribution_result: [],
        _priceId: newPriceId,
        total_quantity: splitQuantity,
        quantity: splitQuantity,
        ...baseMeta
      }

      return { updatedGroup, newGroup }
    },
    [distributionManager]
  )

  const handleSplitPriceGroup = useCallback(() => {
    if (isReadOnly) return
    const { item, priceId, quantity } = splitDialogState
    if (!item || !priceId) return

    const parseNum = (val) => {
      const s = String(val ?? '')
        .trim()
        .replace(/,/g, '')
      const n = Number(s)
      return Number.isFinite(n) ? n : 0
    }

    const splitValue = parseNum(quantity)
    if (splitValue <= 0) {
      setSplitDialogState((prev) => ({
        ...prev,
        error: '数量は正しい数値で入力してください。'
      }))
      return
    }

    // Get the edited quantity value from distributionManager (current UI state)
    const parentNiukabango =
      item._parentNiukabango ??
      item._originalNiukabango ??
      String(item.niukabango).split('__')[0]
    const groupNiukabango = `${parentNiukabango}__p_${priceId}`
    const hinmeiCode = item.hinmeiCode

    // Try to get edited quantity from distributionManager first, then fallback to item value
    const editedQuantity =
      distributionManager.getFixedField(
        hinmeiCode,
        groupNiukabango,
        'quantity',
        undefined
      ) ?? item.quantity

    const currentQuantity = parseNum(editedQuantity)
    if (currentQuantity <= 0) {
      setSplitDialogState((prev) => ({
        ...prev,
        error: '分割対象の数量が無効です。'
      }))
      return
    }

    if (splitValue >= currentQuantity) {
      setSplitDialogState((prev) => ({
        ...prev,
        // error: `分割後の数量は現在の数量 (${currentQuantity}) より小さくなければなりません。`
        error: '入力数量元数量以上です。'
      }))
      return
    }

    const remainingQuantity = currentQuantity - splitValue
    const quantityUnitRaw =
      distributionManager.getFixedField(
        hinmeiCode,
        groupNiukabango,
        'quantity_unit',
        undefined
      ) ??
      item.quantity_unit ??
      0
    const quantityUnit = Number(quantityUnitRaw) || 0
    if (quantityUnit > 0) {
      const remainAfterSplit = currentQuantity - splitValue
      if (
        splitValue % quantityUnit !== 0 ||
        remainAfterSplit % quantityUnit !== 0
      ) {
        setSplitDialogState((prev) => ({
          ...prev,
          error:
            '各数量は入数で割り切れる必要があります。\n再入力してください。'
        }))
        return
      }
    }

    const rowKey = getParentRowKey(item)
    const isAdditionalRow = additionalRows.some(
      (r) => getRowKeyForParent(r) === rowKey
    )

    // Generate a fixed ID for the new group to ensure Redo is deterministic
    const fixedNewPriceId = `p_split_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`

    // Helper functions to fetch the latest values from distributionManager
    const getSyncedGroup = (group) => {
      const synced = { ...group }
      const parentNiukabango =
        item._parentNiukabango ??
        item._originalNiukabango ??
        String(item.niukabango).split('__')[0]
      const groupNiukabango = `${parentNiukabango}__p_${group._priceId}`
      const hinmeiCode = item.hinmeiCode

      const fields = [
        'input_price',
        'output_price',
        'profit',
        'total_quantity',
        'quantity',
        'shipper_code',
        'shipper_name',
        'hinmeiCode',
        'hinmeiName',
        'product_code',
        'product_name',
        'shipping_mark',
        'grade',
        'size',
        'package',
        'weight',
        'quantity_unit',
        'remain_quantity',
        'package_quantity',
        'listing_quantity'
      ]

      fields.forEach((field) => {
        const val = distributionManager.getFixedField(
          hinmeiCode,
          groupNiukabango,
          field,
          undefined
        )
        if (val !== undefined && val !== null && val !== '') {
          synced[field] = val
        }
      })
      return synced
    }

    const getSyncedParent = (pItem) => {
      const synced = { ...pItem }
      const parentNiukabango =
        pItem._parentNiukabango ??
        pItem._originalNiukabango ??
        String(pItem.niukabango).split('__')[0]
      const hinmeiCode = pItem.hinmeiCode

      const fields = [
        'input_price',
        'output_price',
        'profit',
        'total_quantity',
        'quantity',
        'shipper_code',
        'shipper_name',
        'hinmeiCode',
        'hinmeiName',
        'product_code',
        'product_name',
        'shipping_mark',
        'grade',
        'size',
        'package',
        'weight',
        'quantity_unit',
        'remain_quantity',
        'package_quantity',
        'listing_quantity'
      ]

      fields.forEach((field) => {
        const val = distributionManager.getFixedField(
          hinmeiCode,
          parentNiukabango,
          field,
          undefined
        )
        if (val !== undefined && val !== null && val !== '') {
          synced[field] = val
        }
      })
      return synced
    }

    // Helper to sync and split the price_groups array
    const updateGroups = (priceGroups) => {
      if (!priceGroups || priceGroups.length === 0) return priceGroups

      const syncedGroups = priceGroups.map((g) => getSyncedGroup(g))
      const groupIndex = syncedGroups.findIndex((p) => p._priceId === priceId)
      if (groupIndex === -1) return priceGroups

      const sourceGroup = syncedGroups[groupIndex]
      const parentSynced = getSyncedParent(item)

      // Call the pure helper function to execute split calculation
      const { updatedGroup, newGroup } = createSplitGroup(
        sourceGroup,
        parentSynced,
        splitValue,
        fixedNewPriceId
      )

      const nextGroups = [...syncedGroups]
      nextGroups[groupIndex] = updatedGroup
      nextGroups.splice(groupIndex + 1, 0, newGroup)
      return nextGroups
    }

    // Synchronously calculate next states
    let nextInventoryData = structuredClone(inventoryData)
    let nextAdditionalRows = structuredClone(additionalRows)
    let nextRowOrder = structuredClone(rowOrder)

    if (isAdditionalRow) {
      nextAdditionalRows = nextAdditionalRows.map((r) => {
        if (getRowKeyForParent(r) !== rowKey) return r
        return { ...r, price_groups: updateGroups(r.price_groups) }
      })
    } else {
      const hinmeiRows = [...(nextInventoryData[item.hinmeiCode] || [])]
      const nyukaIdx = hinmeiRows.findIndex(
        (r) =>
          String(r.niukabango) ===
          String(item._originalNiukabango || item._parentNiukabango)
      )
      if (nyukaIdx !== -1) {
        const targetNyuka = { ...hinmeiRows[nyukaIdx] }
        const newItems = [...(targetNyuka.items || [])]
        const targetItem = newItems[item._itemIndex]
        if (targetItem) {
          const updatedTargetItem = {
            ...targetItem,
            price_groups: updateGroups(targetItem.price_groups)
          }
          newItems[item._itemIndex] = updatedTargetItem
          targetNyuka.items = newItems
          hinmeiRows[nyukaIdx] = targetNyuka
          nextInventoryData[item.hinmeiCode] = hinmeiRows
        }
      }
    }

    const oldKey = getRowKey(item)
    const idx = nextRowOrder.indexOf(oldKey)
    if (idx !== -1 && fixedNewPriceId) {
      const parentNiuka =
        item._parentNiukabango ??
        item._originalNiukabango ??
        String(item.niukabango).split('__')[0]
      const newKey = `${item.hinmeiCode}::${parentNiuka}__p_${fixedNewPriceId}`
      nextRowOrder.splice(idx + 1, 0, newKey)
    }

    // Capture the BEFORE state snapshot
    const beforeState = captureAppState(inventoryData, additionalRows, rowOrder)

    // Clear stale edits BEFORE capturing AFTER state so they don't override the freshly split values
    const parentNiuka =
      item._parentNiukabango ??
      item._originalNiukabango ??
      String(item.niukabango).split('__')[0]
    const groupNiuka = `${parentNiuka}__p_${priceId}`
    const pRowKey = `${hinmeiCode}::${parentNiuka}`
    const gRowKey = `${hinmeiCode}::${groupNiuka}`

    const fieldsToClear = [
      'input_price',
      'output_price',
      'profit',
      'total_quantity',
      'quantity',
      'shipper_code',
      'shipper_name',
      'hinmeiCode',
      'hinmeiName',
      'product_code',
      'product_name',
      'shipping_mark',
      'grade',
      'size',
      'package',
      'weight',
      'quantity_unit',
      'remain_quantity',
      'package_quantity',
      'listing_quantity'
    ]

    fieldsToClear.forEach((f) => {
      const fixedFieldKey = `__fixed_${f}`
      if (
        distributionManager.data[pRowKey] &&
        fixedFieldKey in distributionManager.data[pRowKey]
      ) {
        delete distributionManager.data[pRowKey][fixedFieldKey]
        if (Object.keys(distributionManager.data[pRowKey]).length === 0) {
          delete distributionManager.data[pRowKey]
        }
      }
      if (
        distributionManager.data[gRowKey] &&
        fixedFieldKey in distributionManager.data[gRowKey]
      ) {
        delete distributionManager.data[gRowKey][fixedFieldKey]
        if (Object.keys(distributionManager.data[gRowKey]).length === 0) {
          delete distributionManager.data[gRowKey]
        }
      }
    })

    // Capture the AFTER state snapshot (constructed from the synchronously computed next states)
    const afterState = captureAppState(
      nextInventoryData,
      nextAdditionalRows,
      nextRowOrder
    )

    console.log(
      'Capture checks: beforeState !== afterState:',
      beforeState !== afterState
    )
    console.log('beforeState.rowOrder:', beforeState.rowOrder)
    console.log('afterState.rowOrder:', afterState.rowOrder)
    console.groupEnd()

    // Apply the updates to React state
    setInventoryData(nextInventoryData)
    setAdditionalRows(nextAdditionalRows)
    setRowOrder(nextRowOrder)
    setForceUpdate((v) => v + 1)

    // Register standard undo/redo action using the clean immutable snapshots
    distributionManager.addLogEntry({
      action: `[${distributionManager.username}] ${item.hinmeiCode}-${parentNiukabango} の 数量を分割しました (${currentQuantity} → ${splitValue}と${remainingQuantity})`,
      up: () => restoreAppState(afterState, 'REDO_SPLIT'),
      down: () => restoreAppState(beforeState, 'UNDO_SPLIT')
    })

    setHasUnsavedChanges(true)
    handleSplitDialogClose()
  }, [
    additionalRows,
    inventoryData,
    rowOrder,
    splitDialogState,
    getParentRowKey,
    getRowKeyForParent,
    getRowKey,
    handleSplitDialogClose,
    isReadOnly
  ])

  const handleDeletePrice = useCallback(
    (item, priceId) => {
      if (isReadOnly) return
      const rowKey = getParentRowKey(item)
      const isAdditionalRow = additionalRows.some(
        (r) => getRowKeyForParent(r) === rowKey
      )

      // Snapshot deleted group and its index BEFORE deletion (for restoration)
      let deletedGroup = null
      let deletedIndex = -1
      if (isAdditionalRow) {
        const row = additionalRows.find((r) => getRowKeyForParent(r) === rowKey)
        if (row) {
          deletedIndex = (row.price_groups || []).findIndex(
            (p) => p._priceId === priceId
          )
          deletedGroup = JSON.parse(
            JSON.stringify((row.price_groups || [])[deletedIndex])
          )
        }
      } else {
        const hinmeiRows = inventoryData[item.hinmeiCode] || []
        const nyuka = hinmeiRows.find(
          (r) => String(r.niukabango) === String(item._originalNiukabango)
        )
        if (nyuka) {
          const targetItem = (nyuka.items || [])[item._itemIndex]
          if (targetItem) {
            deletedIndex = (targetItem.price_groups || []).findIndex(
              (p) => p._priceId === priceId
            )
            deletedGroup = JSON.parse(
              JSON.stringify((targetItem.price_groups || [])[deletedIndex])
            )
          }
        }
      }
      if (!deletedGroup) return

      const doDelete = () => {
        if (isAdditionalRow) {
          setAdditionalRows((prev) =>
            prev.map((r) => {
              if (getRowKeyForParent(r) !== rowKey) return r
              const filtered = (r.price_groups || []).filter(
                (p) => p._priceId !== priceId
              )
              return filtered.length > 0 ? { ...r, price_groups: filtered } : r
            })
          )
        } else {
          setInventoryData((prev) => {
            const newData = { ...prev }
            const hinmeiRows = [...(newData[item.hinmeiCode] || [])]
            const nyukaIdx = hinmeiRows.findIndex(
              (r) => String(r.niukabango) === String(item._originalNiukabango)
            )
            if (nyukaIdx === -1) return prev
            const targetNyuka = { ...hinmeiRows[nyukaIdx] }
            const newItems = [...(targetNyuka.items || [])]
            if (newItems[item._itemIndex]) {
              const targetItem = { ...newItems[item._itemIndex] }
              const filtered = (targetItem.price_groups || []).filter(
                (p) => p._priceId !== priceId
              )
              if (filtered.length === 0) return prev
              targetItem.price_groups = filtered
              newItems[item._itemIndex] = targetItem
            }
            targetNyuka.items = newItems
            hinmeiRows[nyukaIdx] = targetNyuka
            newData[item.hinmeiCode] = hinmeiRows
            return newData
          })
        }
        setForceUpdate((v) => v + 1)
      }

      const doRestore = () => {
        if (isAdditionalRow) {
          setAdditionalRows((prev) =>
            prev.map((r) => {
              if (getRowKeyForParent(r) !== rowKey) return r
              const groups = [...(r.price_groups || [])]
              groups.splice(
                Math.min(deletedIndex, groups.length),
                0,
                JSON.parse(JSON.stringify(deletedGroup))
              )
              return { ...r, price_groups: groups }
            })
          )
        } else {
          setInventoryData((prev) => {
            const newData = { ...prev }
            const hinmeiRows = [...(newData[item.hinmeiCode] || [])]
            const nyukaIdx = hinmeiRows.findIndex(
              (r) => String(r.niukabango) === String(item._originalNiukabango)
            )
            if (nyukaIdx === -1) return prev
            const targetNyuka = { ...hinmeiRows[nyukaIdx] }
            const newItems = [...(targetNyuka.items || [])]
            if (newItems[item._itemIndex]) {
              const targetItem = { ...newItems[item._itemIndex] }
              const groups = [...(targetItem.price_groups || [])]
              groups.splice(
                Math.min(deletedIndex, groups.length),
                0,
                JSON.parse(JSON.stringify(deletedGroup))
              )
              targetItem.price_groups = groups
              newItems[item._itemIndex] = targetItem
            }
            targetNyuka.items = newItems
            hinmeiRows[nyukaIdx] = targetNyuka
            newData[item.hinmeiCode] = hinmeiRows
            return newData
          })
        }
        setForceUpdate((v) => v + 1)
      }

      doDelete()
      const deletedNiukabango =
        item._originalNiukabango ??
        item._parentNiukabango ??
        String(item.niukabango).split('__')[0]
      const deletedQuantity =
        deletedGroup?.quantity ?? deletedGroup?.total_quantity ?? 0
      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${item.hinmeiCode}-${deletedNiukabango} の 行を削除しました (数量: ${deletedQuantity})`,
        up: doDelete,
        down: doRestore
      })
      setHasUnsavedChanges(true)
    },
    [
      additionalRows,
      inventoryData,
      getParentRowKey,
      getRowKeyForParent,
      distributionManager,
      isReadOnly
    ]
  )

  const handleAddBlackCircle = useCallback(() => {
    if (isReadOnly) return
    const BLACK_CIRCLE_CODE = columnManager.BLACK_CIRCLE_CODE
    const SP_BRANCH = 'SP'
    const targetColumnKey = ColumnManager.buildColumnKey(
      BLACK_CIRCLE_CODE,
      SP_BRANCH,
      0
    )

    const beforeConfig = columnManager.exportConfig()

    const existingColumn = columnManager.getCustomerColumns().find((col) => {
      const { customerCode, branchCode } = ColumnManager.parseColumnKey(
        col.customerCode
      )
      const effectiveBranch =
        columnManager.getCustomerHeaderField(col.customerCode, 'edaban') ||
        branchCode
      return customerCode === BLACK_CIRCLE_CODE && effectiveBranch === SP_BRANCH
    })

    if (existingColumn) {
      const currentTorioiki = columnManager.getCustomerHeaderField(
        existingColumn.customerCode,
        'torioiki'
      )
      if (currentTorioiki !== '●') {
        columnManager.setCustomerHeaderField(
          existingColumn.customerCode,
          'torioiki',
          '●'
        )
      }

      const selectedItems = getSelectedItems()
      const { totalAllocated: allocated, actionPayload } =
        distributionManager.autoAllocateRemaining(
          selectedItems,
          existingColumn.customerCode,
          { skipLog: true }
        )

      if (allocated > 0) {
        alert(`${allocated}個を● 3170 SP に取り置きました`)
      } else {
        alert('配分する残数がありません')
      }

      const afterConfig = columnManager.exportConfig()

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] 全部● 操作を実行しました`,
        up: () => {
          columnManager.importConfig(afterConfig)
          if (actionPayload?.up) actionPayload.up()
          setForceUpdate((v) => v + 1)
        },
        down: () => {
          columnManager.importConfig(beforeConfig)
          if (actionPayload?.down) actionPayload.down()
          setForceUpdate((v) => v + 1)
        }
      })

      setForceUpdate((v) => v + 1)
      return
    }

    const selectedItems = getSelectedItems()
    columnManager.addCustomerColumn(targetColumnKey, BLACK_CIRCLE_CODE)
    columnManager.setCustomerHeaderField(targetColumnKey, 'torioiki', '●')
    distributionManager.addCustomerToItems(selectedItems, targetColumnKey, '')

    const { totalAllocated: allocated, actionPayload } =
      distributionManager.autoAllocateRemaining(
        selectedItems,
        targetColumnKey,
        {
          skipLog: true
        }
      )

    if (allocated > 0) {
      alert(`${allocated}個を● 3170 SP に取り置きました`)
    } else {
      alert('配分する残数がありません')
    }

    const afterConfig = columnManager.exportConfig()

    distributionManager.addLogEntry({
      action: `[${distributionManager.username}] 全部● 操作を実行しました`,
      up: () => {
        columnManager.importConfig(afterConfig)
        if (actionPayload?.up) actionPayload.up()
        setForceUpdate((v) => v + 1)
      },
      down: () => {
        columnManager.importConfig(beforeConfig)
        if (actionPayload?.down) actionPayload.down()
        setForceUpdate((v) => v + 1)
      }
    })

    setForceUpdate((v) => v + 1)

    setTimeout(() => {
      const tableScroll = document.querySelector('.rp-table-scroll')
      if (tableScroll)
        tableScroll.scrollTo({
          left: tableScroll.scrollWidth,
          behavior: 'auto'
        })
    }, 100)
  }, [columnManager, distributionManager, getSelectedItems, isReadOnly])

  const handleDeleteColumn = useCallback(
    (columnKey) => {
      if (isReadOnly) return

      const beforeState = captureAppState(
        inventoryData,
        additionalRows,
        rowOrder
      )
      const beforeColumnConfig = columnManager.exportConfig()
      const beforeHiddenColumns = new Set(hiddenColumnKeys)

      const colId = `customer_${columnKey}`
      columnManager.removeColumn(colId)
      distributionManager.removeCustomerFromAllItems(columnKey)

      const nextHiddenColumns = new Set(hiddenColumnKeys)
      nextHiddenColumns.delete(columnKey)
      setHiddenColumnKeys(nextHiddenColumns)

      const { customerCode, branchCode, index } = ColumnManager.isCompositeKey(
        columnKey
      )
        ? ColumnManager.parseColumnKey(columnKey)
        : { customerCode: columnKey, branchCode: '', index: 0 }

      // Keep later same customer/branch columns on their own allocations.
      const cleanDist = (distArray) =>
        removeColumnOccurrence(distArray, customerCode, branchCode, index)

      const nextInventoryData = { ...inventoryData }
      Object.keys(nextInventoryData).forEach((hinmei) => {
        nextInventoryData[hinmei] = nextInventoryData[hinmei].map((nyuka) => ({
          ...nyuka,
          items: nyuka.items?.map((item) => ({
            ...item,
            distribution_result: cleanDist(item.distribution_result),
            price_groups: item.price_groups?.map((pg) => ({
              ...pg,
              distribution_result: cleanDist(pg.distribution_result)
            }))
          })),
          distribution_result: cleanDist(nyuka.distribution_result),
          price_groups: nyuka.price_groups?.map((pg) => ({
            ...pg,
            distribution_result: cleanDist(pg.distribution_result)
          }))
        }))
      })
      setInventoryData(nextInventoryData)

      const nextAdditionalRows = additionalRows.map((row) => ({
        ...row,
        distribution_result: cleanDist(row.distribution_result),
        price_groups: row.price_groups?.map((pg) => ({
          ...pg,
          distribution_result: cleanDist(pg.distribution_result)
        }))
      }))
      setAdditionalRows(nextAdditionalRows)

      const afterState = captureAppState(
        nextInventoryData,
        nextAdditionalRows,
        rowOrder
      )
      const afterColumnConfig = columnManager.exportConfig()

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${formatCode(columnKey)} の 列を削除しました`,
        up: () => {
          restoreAppState(afterState, 'delete_column_redo')
          columnManager.importConfig(afterColumnConfig)
          setHiddenColumnKeys(nextHiddenColumns)
        },
        down: () => {
          restoreAppState(beforeState, 'delete_column_undo')
          columnManager.importConfig(beforeColumnConfig)
          setHiddenColumnKeys(beforeHiddenColumns)
        }
      })

      setForceUpdate((v) => v + 1)
    },
    [
      columnManager,
      distributionManager,
      isReadOnly,
      inventoryData,
      additionalRows,
      rowOrder,
      hiddenColumnKeys
    ]
  )

  const handleGetPrices = useCallback(async () => {
    if (isReadOnly) return
    const selectedItems = getSelectedItems()
    if (selectedItems.length === 0) return

    const draftData = {}
    selectedItems.forEach((item) => {
      const code = item.hinmeiCode
      if (!code) return
      if (!draftData[code]) draftData[code] = []
      draftData[code].push({
        niukabango: item.niukabango,
        grade: item.grade ?? '',
        size: item.size ?? '',
        sanchi_code: item.sanchi_code ?? item.shipper_code ?? ''
      })
    })

    try {
      const priceData = await getPrices(draftData)
      selectedItems.forEach((item) => {
        const matches = priceData[item.hinmeiCode]
        if (!matches) return
        const match = matches.find(
          (p) => String(p.niukabango) === String(item.niukabango)
        )
        if (!match) return
        if (match.input_price !== undefined && match.input_price !== null)
          distributionManager.updateFixedField(
            item.hinmeiCode,
            item.niukabango,
            'input_price',
            match.input_price
          )
        if (match.output_price !== undefined && match.output_price !== null)
          distributionManager.updateFixedField(
            item.hinmeiCode,
            item.niukabango,
            'output_price',
            match.output_price
          )
      })
      setForceUpdate((v) => v + 1)
    } catch (error) {
      console.error('Failed to get prices:', error)
      alert('価格の取得に失敗しました。')
    }
  }, [getSelectedItems, distributionManager, isReadOnly])

  const getActiveCustomerColumns = useCallback(
    (items = getSelectedItems()) =>
      columnManager
        .getCustomerColumns()
        .filter((col) =>
          items.some((item) =>
            distributionManager.hasCustomerInItem(item, col.customerCode)
          )
        ),
    [columnManager, distributionManager, getSelectedItems]
  )

  const validateAllCustomerCodes = useCallback(async () => {
    const selectedItems = getSelectedItems()
    const customerColumns = getActiveCustomerColumns(selectedItems)

    const duplicate = columnManager.getDuplicateCustomerDestination()
    if (duplicate) {
      showToast(
        '取置・場所・枝番・買受人の組み合わせが重複しています。4項目のうち少なくとも1つを変更してください。',
        'error'
      )
      return false
    }

    const codeSet = new Set()
    customerColumns.forEach((col) => {
      const { customerCode } = ColumnManager.parseColumnKey(col.customerCode)
      if (customerCode) codeSet.add(String(customerCode))
    })

    const codes = Array.from(codeSet)
    for (const code of codes) {
      const localError = validateCustomerCodeLocal(code)
      if (localError) {
        showToast(localError, 'error')
        return false
      }
    }

    if (codes.length === 0) return true

    try {
      const validation = await validateCustomerCodes(codes, sellerCode)
      if (!validation?.valid) {
        const firstError =
          Object.values(validation?.errors || {})[0] || '顧客コードが無効です。'
        showToast(firstError, 'error')
        return false
      }
    } catch (error) {
      showToast('顧客コードの確認に失敗しました。', 'error')
      return false
    }

    // Validate branch codes against DB
    const branchPairs = customerColumns
      .map((col) => {
        const { customerCode: code } = ColumnManager.parseColumnKey(
          col.customerCode
        )
        const edaban = String(
          columnManager.getCustomerHeaderField(col.customerCode, 'edaban') ||
            ColumnManager.parseColumnKey(col.customerCode).branchCode ||
            ''
        )
        return { customer_code: code, branch_code: edaban }
      })
      .filter((p) => p.customer_code && p.branch_code)

    if (branchPairs.length > 0) {
      try {
        const branchValidation = await validateBranchCodes(branchPairs)
        if (!branchValidation?.valid) {
          const firstError =
            Object.values(branchValidation?.errors || {})[0] ||
            '枝番が無効です。'
          showToast(firstError, 'error')
          return false
        }
      } catch (error) {
        showToast('枝番の確認に失敗しました。', 'error')
        return false
      }
    }

    return true
  }, [
    columnManager,
    distributionManager,
    getActiveCustomerColumns,
    getSelectedItems,
    showToast,
    sellerCode
  ])

  const handleDraftSaveError = useCallback(
    (error) => {
      let displayMsg = '保存に失敗しました。'
      const errors = error?.data?.errors || {}
      const errorKeys = Object.keys(errors)
      const firstErrorKey = errorKeys[0]

      if (firstErrorKey) {
        displayMsg = errors[firstErrorKey] || displayMsg
      } else if (error?.message) {
        displayMsg = error.message
      }

      showToast(displayMsg, 'error')
    },
    [showToast]
  )

  const applySavedDraftPayload = useCallback(
    (inputData, nextAdditionalRows, accessOverrides = {}) => {
      const savedPayload = createEditPayload(inputData)
      if (!savedPayload) return

      const payloadKey = searchParams.get('payloadKey')
      if (!payloadKey) return

      try {
        const stored = sessionStorage.getItem(payloadKey)
        const payload = stored ? JSON.parse(stored) : {}
        sessionStorage.setItem(
          payloadKey,
          JSON.stringify({
            ...payload,
            ...savedPayload,
            ui_metadata: inputData?.result?.data?.ui_metadata || {},
            productName:
              payload.productName ||
              getSafeProductName(inputData?.result?.data),
            saleMan: payload.saleMan,
            orders: inputData?.result?.orders ?? payload.orders ?? [],
            seller_code: payload.seller_code,
            distributionData: {},
            additionalRows: nextAdditionalRows,
            ...accessOverrides
          })
        )
        if (inputData?.result?.data?.ui_metadata) {
          setInitialUiMetadataPresent(true)
        }
      } catch (e) {
        console.error('Failed to update saved draft session payload:', e)
      }
    },
    [searchParams, setInitialUiMetadataPresent]
  )

  // After 荷割確定 the table must continue from exactly what was stored, as if
  // the draft had just been reopened (it is read-only, so no Undo is needed).
  // Otherwise React keeps the rows it loaded first and shows pre-save values.
  const rebaseToSavedDraft = useCallback(
    (inputData) => {
      const saved = createEditPayload(inputData)
      if (!saved) return
      distributionManager.setData({})
      distributionManager.draftCells = {}
      distributionManager.resetCells.clear()
      // The saved config uses normalized column keys (see
      // normalizeSavedColumnMetadata), matching the saved rows.
      if (saved.columnConfig) columnManager.importConfig(saved.columnConfig)
      if (saved.hiddenColumnKeys) {
        setHiddenColumnKeys(new Set(saved.hiddenColumnKeys))
      }
      isFirstLoadRef.current = true
      savedDraftVersionRef.current += 1
      sessionHoldsSavedDraftRef.current = false
      setInventoryData(saved.inventoryData)
      // Additional rows are part of product_list once saved.
      setAdditionalRows([])
    },
    [columnManager, distributionManager]
  )

  const handleConfirm = useCallback(async () => {
    if (isReadOnly) return
    const customerColumns = getActiveCustomerColumns()

    const hasTorioiki = customerColumns.some((col) => {
      const torioiki = columnManager.getCustomerHeaderField(
        col.customerCode,
        'torioiki'
      )
      return torioiki && torioiki !== ''
    })

    const doSave = async (isConfirm = false) => {
      let saveData = null
      let inputData = null
      try {
        const draftId =
          searchParams.get('draftId') ||
          searchParams.get('id') ||
          'test-draft-id'

        const unnamedColumn = getActiveCustomerColumns().find(
          (col) => col.customerCode.startsWith('NEW_') || col.label === ''
        )
        if (unnamedColumn) {
          showToast(
            '顧客コードが未入力の列があります。入力してから保存してください。',
            'error'
          )
          return
        }

        const isValid = await validateAllCustomerCodes()
        if (!isValid) return

        setIsLoading(true)
        // Final sync before save to ensure all nyuka_index updates are in distributionManager
        const finalItems = getSelectedItems()
        if (distributionManager.hasMissingCustomerCode(finalItems, columnManager)) {
          showToast(
            '顧客コードが未入力の列があります。入力してから保存してください。',
            'error'
          )
          setIsLoading(false)
          return
        }
        if (!distributionManager.canSave(finalItems, columnManager)) {
          showToast('残数が0になるように配分数量を調整してください。', 'error')
          return
        }
        const selectedNyukabangoForUpload = Array.from(
          new Set(
            finalItems
              .map((item) => item?._originalNiukabango ?? item?.niukabango)
              .map((v) => String(v).trim())
              .filter((v) => v)
          )
        )
        distributionManager.syncItems(finalItems)

        saveData = distributionManager.getDataForSave(columnManager)
        const metadata = {
          columnConfig: columnManager.exportConfig(),
          selectedNyukaNumbers: selectedNyukaNumbers,
          hiddenColumnKeys: Array.from(hiddenColumnKeys),
          rowOrder: rowOrder,
          hiddenRowKeys: Array.from(hiddenRowKeys),
          hiddenHeaderFieldKeys: Array.from(hiddenHeaderFieldKeys)
        }
        inputData = transformToApiFormat(
          saveData.currentItems,
          isConfirm,
          metadata
        )

        const validation = validatePriceGroups(inputData)
        if (!validation.valid) {
          showToast(validation.errors.join('\n'), 'error')
          setIsLoading(false)
          return
        }

        await updateDraft({ draftId, inputData })
        rebaseToSavedDraft(inputData)
        applySavedDraftPayload(inputData, [], {
          readOnly: true,
          confirmState: 'confirmed',
          canExportExcel: true,
          canExportCsv: true,
          canExportDbExcel: true
        })
        setHasUnsavedChanges(false)
        setDraftAccess({
          editable: false,
          confirmState: 'confirmed',
          canExportExcel: true,
          canExportCsv: true,
          canExportDbExcel: true
        })
        setConfirmedLocked(true)
        setForceUpdate((v) => v + 1)
        try {
          const res = await uploadDraftToGcpCSV(
            draftId,
            selectedNyukabangoForUpload
          )
          if (res?.message) {
            showToast(`設定しました！${res.message}`, 'success')
          } else {
            showToast(
              '荷割結果を確定しました。\nCSVファイルをGCPにアップロードしました。',
              'success'
            )
          }
        } catch (uploadError) {
          console.error(
            'Failed to upload confirmed draft CSV to GCP:',
            uploadError
          )
          showToast(
            '設定しましたが、CSVのGCPアップロードに失敗しました。',
            'error'
          )
        }
      } catch (error) {
        console.error('Failed to save draft:', error)
        handleDraftSaveError(error)
      } finally {
        setIsLoading(false)
      }
    }

    if (hasTorioiki) {
      setConfirmDialogForConfirm({
        open: true,
        onConfirm: () => {
          setConfirmDialogForConfirm({ open: false, onConfirm: null })
          doSave(true)
        }
      })
    } else {
      doSave(true)
    }
  }, [
    searchParams,
    distributionManager,
    columnManager,
    getActiveCustomerColumns,
    getSelectedItems,
    flattenItems,
    validateAllCustomerCodes,
    handleDraftSaveError,
    applySavedDraftPayload,
    rebaseToSavedDraft,
    showToast,
    rowOrder,
    hiddenRowKeys,
    hiddenHeaderFieldKeys,
    hiddenColumnKeys,
    selectedNyukaNumbers,
    updateDraft,
    isReadOnly
  ])
  const getSelectedNyukabangoForExport = useCallback(() => {
    const values = getSelectedItems()
      .map((item) => item?._originalNiukabango ?? item?.niukabango)
      .map((v) => String(v).trim())
      .filter((v) => v)

    return Array.from(new Set(values))
  }, [getSelectedItems])

  const handleDownloadExcel = useCallback(async () => {
    if (!canDownloadExcel) return
    const draftId = searchParams.get('draftId') || searchParams.get('id')
    try {
      setIsLoading(true)
      const { blob, contentDisposition } = await exportDraftToExcel(
        draftId,
        getSelectedNyukabangoForExport()
      )

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const filename = extractFilenameFromContentDisposition(
        contentDisposition,
        draftId
      )
      link.setAttribute('download', filename)

      document.body.appendChild(link)
      link.click()
      if (link.parentNode) link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading Excel file:', error)
    } finally {
      setIsLoading(false)
    }
  }, [canDownloadExcel, searchParams, getSelectedNyukabangoForExport])

  const handleDownloadDB = useCallback(
    async (type) => {
      if (!canDownloadFinalFiles) return
      const draftId = searchParams.get('draftId') || searchParams.get('id')
      try {
        setIsLoading(true)

        if (type === 'excel_gcp') {
          const res = await uploadDraftToGcpDBExcel(
            draftId,
            getSelectedNyukabangoForExport()
          )
          if (res?.status === 204) {
            showToast('GCPへアップロードしました。', 'success')
          } else if (res?.message) {
            showToast(res.message, 'success')
          } else {
            showToast('GCPへアップロードしました。', 'success')
          }
          return
        }

        const { blob, contentDisposition } = await exportDraftToDBExcel(
          draftId,
          getSelectedNyukabangoForExport()
        )

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          extractFilenameFromContentDisposition(contentDisposition, draftId)
        )
        document.body.appendChild(link)
        link.click()
        if (link.parentNode) link.parentNode.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('Error exporting DB file:', error)
        showToast('出力に失敗しました。もう一度お試しください。', 'error')
      } finally {
        setIsLoading(false)
      }
    },
    [
      canDownloadFinalFiles,
      searchParams,
      getSelectedNyukabangoForExport,
      showToast
    ]
  )

  const handleSave = useCallback(async () => {
    if (isReadOnly) return
    const customerColumns = getActiveCustomerColumns()

    const hasTorioiki = customerColumns.some((col) => {
      const torioiki = columnManager.getCustomerHeaderField(
        col.customerCode,
        'torioiki'
      )
      return torioiki && torioiki !== ''
    })

    const doSave = async () => {
      let saveData = null
      let inputData = null
      try {
        const draftId =
          searchParams.get('draftId') ||
          searchParams.get('id') ||
          'test-draft-id'

        const unnamedColumn = getActiveCustomerColumns().find(
          (col) => col.customerCode.startsWith('NEW_') || col.label === ''
        )
        if (unnamedColumn) {
          showToast(
            '顧客コードが未入力の列があります。入力してから保存してください。',
            'error'
          )
          return
        }

        const isValid = await validateAllCustomerCodes()
        if (!isValid) return

        setIsLoading(true)
        const finalItems = getSelectedItems()
        if (distributionManager.hasMissingCustomerCode(finalItems, columnManager)) {
          showToast(
            '顧客コードが未入力の列があります。入力してから保存してください。',
            'error'
          )
          setIsLoading(false)
          return
        }
        if (!distributionManager.canSave(finalItems, columnManager)) {
          showToast('残数が0になるように配分数量を調整してください。', 'error')
          return
        }
        distributionManager.syncItems(finalItems)

        saveData = distributionManager.getDataForSave(columnManager)
        const metadata = {
          columnConfig: columnManager.exportConfig(),
          selectedNyukaNumbers: selectedNyukaNumbers,
          hiddenColumnKeys: Array.from(hiddenColumnKeys),
          rowOrder: rowOrder,
          hiddenRowKeys: Array.from(hiddenRowKeys),
          hiddenHeaderFieldKeys: Array.from(hiddenHeaderFieldKeys)
        }
        inputData = transformToApiFormat(saveData.currentItems, false, metadata)

        const validation = validatePriceGroups(inputData)
        if (!validation.valid) {
          showToast(validation.errors.join('\n'), 'error')
          setIsLoading(false)
          return
        }

        await updateDraft({ draftId, inputData })

        // Keep the managers and Undo history; only sessionStorage switches to
        // the saved draft, so a reload reopens exactly what was stored.
        savedDraftVersionRef.current += 1
        sessionHoldsSavedDraftRef.current = true
        applySavedDraftPayload(inputData, [], {
          readOnly: false,
          confirmState: 'draft',
          canExportExcel: true,
          canExportCsv: false,
          canExportDbExcel: false
        })
        setHasUnsavedChanges(false)
        setDraftAccess({
          editable: true,
          confirmState: 'draft',
          canExportExcel: true,
          canExportCsv: false,
          canExportDbExcel: false
        })
        setConfirmedLocked(false)
        showToast('保存しました！', 'success')
      } catch (error) {
        console.error('Failed to save draft:', error)
        handleDraftSaveError(error)
      } finally {
        setIsLoading(false)
      }
    }

    if (hasTorioiki) {
      setConfirmDialogForSave({
        open: true,
        onConfirm: () => {
          setConfirmDialogForSave({ open: false, onConfirm: null })
          doSave()
        }
      })
    } else {
      doSave()
    }
  }, [
    searchParams,
    distributionManager,
    columnManager,
    getActiveCustomerColumns,
    getSelectedItems,
    flattenItems,
    validateAllCustomerCodes,
    handleDraftSaveError,
    applySavedDraftPayload,
    showToast,
    rowOrder,
    hiddenRowKeys,
    hiddenHeaderFieldKeys,
    hiddenColumnKeys,
    selectedNyukaNumbers,
    updateDraft,
    isReadOnly
  ])

  const handleLogClear = useCallback(
    () => distributionManager.clearLog(),
    [distributionManager]
  )

  const handleLogToggle = useCallback(() => {
    const next = !isLogOpen
    setIsLogOpen(next)
    if (next) setIsOrderOpen(false)
    eventBus.emit(EVENTS.LOG_PANEL_TOGGLED, { isOpen: next })
  }, [isLogOpen])

  const handleOrderToggle = useCallback(() => {
    const next = !isOrderOpen
    setIsOrderOpen(next)
    if (next) setIsLogOpen(false)
    eventBus.emit(EVENTS.ORDER_PANEL_TOGGLED, { isOpen: next })
  }, [isOrderOpen])
  const handleBack = useCallback(() => {
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.location.href = '/home/schedule?activeTab=history'
        window.close()
        return
      } catch (e) {
        console.error('Failed to navigate parent to history:', e)
      }
    }
    navigate('/home/schedule?activeTab=history')
  }, [navigate])

  const handleReturn = useCallback(() => {
    if (isReadOnly) return
    const selectedItems = getSelectedItems()
    if (selectedItems.length === 0) {
      alert('入荷番号を選択してください。')
      return
    }
    const returned = distributionManager.returnNonStatus3ToRemain(selectedItems)
    if (returned > 0) {
      alert(`${returned}個を残数に戻しました。`)
      setForceUpdate((v) => v + 1)
    } else {
      alert('戻す配分がありません。')
    }
  }, [distributionManager, getSelectedItems, isReadOnly])

  const handleClose = useCallback(() => window.close(), [])

  const handleUndo = useCallback(() => {
    if (isReadOnly) return
    distributionManager.changeLogUndo()
  }, [distributionManager, isReadOnly])

  const handleRedo = useCallback(() => {
    if (isReadOnly) return
    distributionManager.changeLogRedo()
  }, [distributionManager, isReadOnly])

  const handleRowReorder = useCallback(
    (fromIdx, toIdx, selectedRowIndexes) => {
      if (isReadOnly) return
      const currentItems = getSelectedItems()
      const oldOrder = [...rowOrder]

      const normalizedSelection =
        Array.isArray(selectedRowIndexes) && selectedRowIndexes.length > 0
          ? selectedRowIndexes
          : [fromIdx]

      const newItems = reorderSelection(
        currentItems,
        normalizedSelection,
        fromIdx,
        toIdx
      )
      const newOrder = newItems.map((item) => getRowKey(item))
      setRowOrder(newOrder)
      setHasUnsavedChanges(true)

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] 行の並び順を変更しました`,
        up: () => {
          setRowOrder(newOrder)
          setForceUpdate((v) => v + 1)
        },
        down: () => {
          setRowOrder(oldOrder)
          setForceUpdate((v) => v + 1)
        }
      })
    },
    [getSelectedItems, rowOrder, distributionManager, getRowKey, isReadOnly]
  )

  const handleReAllocate = useCallback(async () => {
    if (isReadOnly) return
    try {
      setIsLoading(true)
      const draftId =
        searchParams.get('draftId') || searchParams.get('id') || 'test-draft-id'
      await reAllocateProducts(draftId)
    } catch (error) {
      console.error('Failed to re-allocate:', error)
      alert('再配分に失敗しました。もう一度お試しください。')
    } finally {
      setIsLoading(false)
    }
  }, [searchParams, isReadOnly])

  return (
    <div className='rp-container relative'>
      {isLoading && (
        <div className='absolute top-0 left-0 bg-gray-200 opacity-50 w-full h-full flex justify-center items-center z-[20000]'>
          <CircularProgress color='inherit' />
        </div>
      )}
      <Dialog
        open={splitDialogState.open}
        onClose={handleSplitDialogClose}
        PaperProps={{
          sx: {
            width: 600,
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <DialogTitle
            sx={{
              p: 0,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            数量入力画面
          </DialogTitle>

          <IconButton
            size='small'
            onClick={handleSplitDialogClose}
            sx={{
              color: 'text.secondary'
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Typography
            variant='body2'
            sx={{
              // color: 'text.secondary',
              lineHeight: 1.7,
              fontWeight: 900,
              fontSize: '0.95rem'
            }}
          >
            挿入する行の数量を入力して下さい。
            <br />
            {splitDialogState.maxQuantity != null && (
              <div
                style={{
                  color: '#1976d2',
                  fontWeight: 600,
                  marginTop: '8px',
                  whiteSpace: 'pre-wrap'
                }}
              >
                (元数量 = {splitDialogState.maxQuantity})
              </div>
            )}
          </Typography>

          <TextField
            autoFocus
            fullWidth
            size='small'
            type='text'
            placeholder='数量を入力'
            value={splitDialogState.quantity}
            onKeyDown={(e) => {
              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                e.preventDefault()
              }
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSplitPriceGroup()
              }
            }}
            onChange={(e) => {
              const val = e.target.value.replace(/[^0-9]/g, '')
              if (val === '0') return
              setSplitDialogState((prev) => ({
                ...prev,
                quantity: val,
                error: ''
              }))
            }}
            inputProps={{ min: 0, step: 'any' }}
            error={Boolean(splitDialogState.error)}
            helperText={splitDialogState.error}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Box>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.5
          }}
        >
          <Button
            variant='outlined'
            onClick={handleSplitDialogClose}
            sx={{
              minWidth: 90,
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            キャンセル
          </Button>

          <Button
            variant='contained'
            onClick={handleSplitPriceGroup}
            sx={{
              minWidth: 90,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 'none'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <div className='rp-surface'>
        <Toolbar
          onBack={handleBack}
          onReserve={handleReturn}
          onSave={handleSave}
          onAddBlackCircle={handleAddBlackCircle}
          onConfirm={handleConfirm}
          onLogClear={handleLogClear}
          onLogToggle={handleLogToggle}
          onOrderToggle={handleOrderToggle}
          onClose={handleClose}
          onUndo={handleUndo}
          onRedo={handleRedo}
          isLogOpen={isLogOpen}
          isOrderOpen={isOrderOpen}
          canSave={canSave}
          isReadOnly={isReadOnly}
          canDownloadExcel={canDownloadExcel}
          canDownloadFinalFiles={canDownloadFinalFiles}
          currentLogIndex={currentLogIndex}
          changeLog={changeLog}
          onGetPrices={handleGetPrices}
          onDownLoadExcel={handleDownloadExcel}
          onReAllocate={handleReAllocate}
          onDownLoadDB={handleDownloadDB}
          hasUnsavedChanges={hasUnsavedChanges}
          farmFilterValues={farmFilterValues}
          farmFilterOptions={farmFilterOptions}
          onFarmFilterChange={setFarmFilterValues}
        />

        <div className='rp-content'>
          <div className='rp-split-container'>
            <DistributionTable
              selectedItems={visibleSelectedItems}
              allItems={allSelectedItems}
              farmFilterValues={farmFilterValues}
              gradeFilterValues={gradeFilterValues}
              sizeFilterValues={sizeFilterValues}
              gradeFilterOptions={gradeFilterOptions}
              sizeFilterOptions={sizeFilterOptions}
              onGradeFilterChange={handleGradeFilterChange}
              onSizeFilterChange={handleSizeFilterChange}
              isFilterLoading={isFilterLoading}
              rowReorderDisabled={
                farmFilterValues.length > 0 ||
                gradeFilterValues.length > 0 ||
                sizeFilterValues.length > 0
              }
              hiddenRowKeys={hiddenRowKeys}
              hiddenColumnKeys={hiddenColumnKeys}
              hiddenHeaderFieldKeys={hiddenHeaderFieldKeys}
              onHideRow={handleHideRow}
              onHideHeaderField={handleHideHeaderField}
              onHideColumn={handleHideColumn}
              onUnhideItem={handleUnhideItem}
              distributionManager={distributionManager}
              columnManager={columnManager}
              todayString={salesDate || todayString}
              onAddNewRow={handleAddNewRow}
              onAddPrice={handleAddPrice}
              onDeletePrice={handleDeletePrice}
              onRowReorder={handleRowReorder}
              onDeleteColumn={handleDeleteColumn}
              hasOriginalOrderQuantity={hasOriginalOrderQuantity}
              layoutUpdate={forceUpdate}
              orders={orders}
              sellerCode={sellerCode}
              productName={productName}
              saleMan={saleMan}
              gradeOptions={gradeOptions}
              sizeOptions={sizeOptions}
              isReadOnly={isReadOnly}
            />

            {isLogOpen && (
              <div
                className='rp-splitter'
                role='separator'
                aria-orientation='vertical'
                tabIndex={0}
              />
            )}

            <LogPanel
              changeLog={changeLog}
              isOpen={isLogOpen}
              width={logPanelWidth}
              currentLogIndex={currentLogIndex}
              onWidthChange={setLogPanelWidth}
              distributionManager={distributionManager}
            />

            <OrderPanel
              customers={orderCustomers}
              isOpen={isOrderOpen}
              productNameByHic={productNameByHic}
            />
          </div>
        </div>
      </div>
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog
        open={confirmDialogForConfirm.open}
        title='取置の確認'
        message='取置が設定されている列があります。このまま確定しますか。'
        confirmText='確定する'
        cancelText='キャンセル'
        onConfirm={confirmDialogForConfirm.onConfirm}
        onCancel={() =>
          setConfirmDialogForConfirm({ open: false, onConfirm: null })
        }
      />
      <ConfirmDialog
        open={confirmDialogForSave.open}
        title='取置の確認'
        message='取置が設定されている列があります。このまま保存しますか。'
        confirmText='保存する'
        cancelText='キャンセル'
        onConfirm={confirmDialogForSave.onConfirm}
        onCancel={() =>
          setConfirmDialogForSave({ open: false, onConfirm: null })
        }
      />
    </div>
  )
}

export default RightPanelPopup
